/* ==========================================================================
   Custom Checkout — PayPal Advanced Card (ACDC)  ·  Phase 1 (sandbox PoC)
   --------------------------------------------------------------------------
   Orchestrates the client side of the custom checkout:
     1. read the Shopify cart (/cart.js)
     2. collect contact + shipping address
     3. call the backend `create-order`  → Shopify draft order (authoritative
        totals) + a matching PayPal order  → returns { paypalOrderId, totals }
     4. mount PayPal Card Fields (hosted iframes — card data never touches us)
     5. on pay → cardField.submit() → onApprove → backend `capture-order`
        → Shopify order created (PAID) → redirect to confirmation

   The card number/expiry/CVV live ONLY inside PayPal's iframes (PCI SAQ A).
   The backend is the sole price authority; nothing here computes money.

   Config comes from data-* attributes on the root element (rendered by
   sections/custom-checkout.liquid). No secrets here — only the public
   PayPal client-id and the backend base URL.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.querySelector('[data-custom-checkout]');
  if (!root) return;

  // Keep small (14px) field fonts without iOS focus-zoom — constrain the viewport (Shopify does this).
  (function fixViewport() {
    var content = 'width=device-width, initial-scale=1, maximum-scale=1';
    var vp = document.querySelector('meta[name="viewport"]');
    if (vp) { vp.setAttribute('content', content); }
    else { vp = document.createElement('meta'); vp.name = 'viewport'; vp.content = content; document.head.appendChild(vp); }
  })();

  // Local-dev overrides, strictly localhost-only.
  //
  // `shopify theme dev` renders the theme with the LIVE settings, which point at
  // the production backend — so pointing the page at a local one used to mean
  // editing a committed default and remembering to put it back. These two query
  // params do the same job without touching a tracked file, and the hostname gate
  // makes them inert on any host a customer could reach:
  //
  //   ?cc_backend=http://127.0.0.1:3001    talk to a local backend
  //   ?cc_gateway=authorize               force the Authorize.Net card rail
  //   ?cc_gateway=stripe                  force the Stripe card rail (dormant —
  //                                       still wired, but no theme setting
  //                                       selects it any more)
  var LOCAL_DEV = /^(localhost|127\.0\.0\.1)$/.test(location.hostname);

  function devOverride(param) {
    if (!LOCAL_DEV) return '';
    try { return new URLSearchParams(location.search).get(param) || ''; }
    catch (e) { return ''; }
  }

  // The overrides live in the URL, so anything that rewrites the URL — or sends
  // the buyer away and back — has to carry them or the return lands on the wrong
  // backend with the wrong gateway.
  // Layout inspection only: force the gateway-dependent rows on screen so the
  // payment list can be reviewed before any account exists. Localhost-gated like
  // the rest, and it reveals rows WITHOUT pretending they can take money — the
  // Apple Pay button is still only mounted when a device can actually pay.
  //   ?cc_rows=all
  function forceRowsVisible() { return devOverride('cc_rows') === 'all'; }

  function devQuery() {
    var keep = [];
    ['cc_backend', 'cc_gateway', 'cc_rows'].forEach(function (k) {
      var v = devOverride(k);
      if (v) keep.push(k + '=' + encodeURIComponent(v));
    });
    return keep.length ? '?' + keep.join('&') : '';
  }

  var CONFIG = {
    clientId:   root.getAttribute('data-paypal-client-id') || '',
    backendUrl: (function () {
      var dev = devOverride('cc_backend');
      if (dev) return dev.replace(/\/+$/, '');
      var u = (root.getAttribute('data-backend-url') || '').replace(/\/+$/, '');
      // Local dev convenience: on the theme dev server, talk to the local backend
      // on the SAME hostname (avoids a 127.0.0.1 vs localhost origin mismatch).
      if (!u && /^(localhost|127\.0\.0\.1)$/.test(location.hostname)) return 'http://' + location.hostname + ':3001';
      return u;
    })(),
    currency:   root.getAttribute('data-currency') || 'USD',
    testMode:   root.getAttribute('data-test-mode') === 'true',
    gmapsKey:   root.getAttribute('data-gmaps-key') || '',
    geoCountry: root.getAttribute('data-geo-country') || '',
    affirmKey:  root.getAttribute('data-affirm-key') || '',
    affirmEnv:  root.getAttribute('data-affirm-env') || 'live',
    // 'authorize' (the default) reads the card out of this page's own inputs
    // with Accept.js; 'paypal' keeps PayPal's hosted card fields. 'stripe' still
    // works and is still here, but the theme editor no longer offers it — only
    // the localhost ?cc_gateway override reaches it. Whichever is live, the
    // PayPal Wallet and Afterpay rows are untouched.
    cardGateway: devOverride('cc_gateway') || root.getAttribute('data-card-gateway') || 'authorize'
  };

  // Endpoints (see docs/custom-checkout-architecture.md §13)
  var ENDPOINTS = {
    quote:        CONFIG.backendUrl + '/api/checkout/quote',
    profileCheck: CONFIG.backendUrl + '/api/checkout/profile-check',
    createOrder:  CONFIG.backendUrl + '/api/checkout/create-order',
    captureOrder: CONFIG.backendUrl + '/api/checkout/capture-order',
    createAfterpay:  CONFIG.backendUrl + '/api/checkout/create-afterpay',
    captureAfterpay: CONFIG.backendUrl + '/api/checkout/capture-afterpay',
    createAffirm:    CONFIG.backendUrl + '/api/checkout/create-affirm',
    captureAffirm:   CONFIG.backendUrl + '/api/checkout/capture-affirm',
    stripeConfig:    CONFIG.backendUrl + '/api/checkout/stripe-config',
    createStripe:    CONFIG.backendUrl + '/api/checkout/create-stripe',
    createStripeForDraft: CONFIG.backendUrl + '/api/checkout/create-stripe-for-draft',
    captureStripe:   CONFIG.backendUrl + '/api/checkout/capture-stripe',
    authnetConfig:   CONFIG.backendUrl + '/api/checkout/authnet-config',
    createAuthnet:   CONFIG.backendUrl + '/api/checkout/create-authnet',
    createAuthnetForDraft: CONFIG.backendUrl + '/api/checkout/create-authnet-for-draft',
    captureAuthnet:  CONFIG.backendUrl + '/api/checkout/capture-authnet',
    railEvent:       CONFIG.backendUrl + '/api/checkout/rail-event',
    loadDraft:          CONFIG.backendUrl + '/api/checkout/load-draft',
    createOrderForDraft: CONFIG.backendUrl + '/api/checkout/create-order-for-draft'
  };

  // Draft-order pay mode: an admin created a draft in Shopify and sent the customer
  // a signed link  /pages/secure-checkout?draft=<numericId>&t=<token>. In this mode
  // the line items + totals are LOCKED to the draft (no cart, no re-quote); the
  // customer just pays. createOrder() routes to create-order-for-draft; capture is
  // the same capture-order path. Requires both params (the token is verified server-side).
  var DRAFT = (function () {
    try {
      var q = new URLSearchParams(location.search);
      var id = q.get('draft'), t = q.get('t');
      return (id && t) ? { id: id, token: t } : null;
    } catch (e) { return null; }
  })();

  // Runtime state for a single checkout attempt.
  var state = {
    cart: null,
    cartLoaded: false,
    payReady: false,
    railError: false,
    sdkPromise: null,
    paypalOrderId: null,
    draftOrderId: null,
    totals: null,
    cardField: null,
    submitting: false,
    affirmLoading: false,
    ppInFlight: false,
    afterpayLoading: false,
    afterpayInitDone: false,
    afterpayToken: null,
    // The draft THIS Afterpay attempt built, kept apart from state.draftOrderId
    // so a bounce that happens before create-afterpay resolves reports nothing
    // rather than pinning itself to a previous attempt's draft.
    afterpayDraftId: null,
    // Same idea for the PayPal button: the draft THIS PayPal attempt built. A
    // buyer can close the popup before createOrder resolves, and state.draftOrderId
    // may still hold the draft another rail made a minute earlier.
    ppDraftId: null,
    stripe: null,
    stripeElements: null,
    stripeCardNumber: null,
    stripePaymentRequest: null,
    stripePaymentIntentId: null,
    stripeBillingDetails: null,
    authnetLoginId: null,
    authnetClientKey: null,
    authnetReady: false,
    discountCodes: [],
    pendingCode: null,
    shipHandle: null,
    shipTitle: null
  };

  // -- tiny DOM helpers ------------------------------------------------------
  function $(sel) { return root.querySelector(sel); }
  function money(value) {
    var n = parseFloat(value || 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: CONFIG.currency }).format(n);
    } catch (e) {
      return CONFIG.currency + ' ' + n.toFixed(2);
    }
  }
  function show(el) { if (el) el.hidden = false; }
  function hide(el) { if (el) el.hidden = true; }
  function setStatus(msg, kind) {
    var box = $('[data-checkout-status]');
    if (!box) return;
    box.textContent = msg || '';
    box.className = 'cc-status' + (kind ? ' cc-status--' + kind : '');
    box.hidden = !msg;
  }

  // Cart news must not paint over payment news. "Your cart is empty" landing a
  // second after "we could not load the payment form" hid the only message that
  // told the customer what to do next.
  function setCartStatus(msg, kind) {
    if (state.railError) return;
    setStatus(msg, kind);
  }
  // Retract a specific message without touching whatever replaced it.
  function clearStatusIf(msg) {
    var box = $('[data-checkout-status]');
    if (box && box.textContent === msg) setStatus('', null);
  }

  // -- analytics -------------------------------------------------------------
  // Shopify's OWN funnel reports (sessions_that_reached_checkout / completed_checkout,
  // conversion rate) can never see this page: the order is created through the Admin
  // API, so its source_name is our app id — `web` is a protected handle no API client
  // can assign. Standard events (`checkout_started`, `checkout_completed`) are equally
  // Shopify-only; merchants may publish CUSTOM events only. So we publish prefixed
  // custom events, which Shopify delivers to every web pixel on the store (PostHog,
  // GA4, Meta) — that is where our funnel actually lives. See snippets/posthog-web-pixel.js.
  // Fail-soft everywhere: analytics must NEVER break a payment.
  // Docs: shopify.dev/docs/api/web-pixels-api/emitting-data
  var ANALYTICS_PREFIX = 'sofatica:';

  function publish(name, data) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(ANALYTICS_PREFIX + name, data);
      }
    } catch (e) {}
  }

  // Mirror the standard-event `checkout.lineItems` shape so pixel subscribers can
  // read custom and native checkouts through the same code path. /cart.js prices
  // are in cents; the standard events use decimal strings.
  function analyticsLineItems() {
    var items = (state.cart && state.cart.items) || [];
    return items.map(function (i) {
      return {
        title: i.product_title || i.title,
        quantity: i.quantity,
        variant: {
          id: i.variant_id,
          title: i.variant_title,
          price: { amount: ((i.final_price || 0) / 100).toFixed(2), currencyCode: CONFIG.currency },
          product: { id: i.product_id, title: i.product_title || i.title }
        }
      };
    });
  }

  function amount(v) { return v == null ? null : String(v); }

  // Emitted once the cart is on screen — the custom-checkout equivalent of
  // `checkout_started`. Totals aren't quoted yet here, so we use the cart's own.
  function publishCheckoutStarted() {
    var cart = state.cart;
    publish('checkout_started', {
      checkout: {
        token: null,
        currencyCode: (cart && cart.currency) || CONFIG.currency,
        totalPrice: { amount: cart ? ((cart.total_price || 0) / 100).toFixed(2) : null },
        lineItems: analyticsLineItems()
      },
      checkout_source: 'custom'
    });
  }

  // The purchase event. `order` is the backend's created Shopify order.
  function publishCheckoutCompleted(order) {
    var t = state.totals || {};
    publish('checkout_completed', {
      checkout: {
        token: state.draftOrderId || state.paypalOrderId || null,
        email: val('email') || null,
        currencyCode: t.currency || CONFIG.currency,
        totalPrice: { amount: amount(t.total) },
        subtotalPrice: { amount: amount(t.subtotal) },
        totalTax: { amount: amount(t.tax) },
        shippingLine: { price: { amount: amount(t.shipping) } },
        order: {
          id: (order && order.id) || null,
          name: (order && order.name) || null
        },
        lineItems: analyticsLineItems()
      },
      payment_method: formatPaymentMethod(state.paymentMethod),
      shipping_method: state.shipTitle || null,
      checkout_source: 'custom'
    });
  }

  // -- 1. read the Shopify cart ---------------------------------------------
  // One retry, because this fetch is not always answered by Shopify: the domain
  // sits behind a proxy that can return a challenge page or a 429 under load, and
  // a non-JSON body used to surface as "something went wrong loading checkout".
  // A single retry 800ms later clears the transient ones.
  function fetchCart(isRetry) {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('cart-' + r.status);
        return r.json();
      })
      .catch(function (err) {
        if (isRetry) throw err;
        return new Promise(function (res) { setTimeout(res, 800); }).then(function () {
          return fetchCart(true);
        });
      });
  }

  function loadCart() {
    return fetchCart(false).then(function (cart) {
      state.cart = cart;
      state.cartLoaded = true;
      renderCartSummary(cart);
      return cart;
    });
  }

  // Custom checkout only prices SINGLE shipping-profile carts accurately. A cart
  // whose items span multiple delivery profiles splits into several shipments in
  // Shopify's native checkout — which we cannot faithfully reproduce — so it is
  // sent to the native checkout instead.
  function checkCartProfiles(cart) {
    if (!CONFIG.backendUrl || !cart || !cart.items || !cart.items.length) return;
    var ids = cart.items.map(function (i) { return i.variant_id; });
    fetch(ENDPOINTS.profileCheck, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ variantIds: ids })
    })
      .then(function (r) { return r.json(); })
      .then(function (d) { if (d && d.single === false) onMixedProfile(); })
      .catch(function () { /* fail open — a check error must not block checkout */ });
  }
  // A cart spanning more than one Shopify delivery profile. Shopify splits it
  // into separate shipments at fulfilment, and a draft order carries only ONE
  // shipping line — which is why, until 2026-08-28, this handed the buyer to
  // Shopify's NATIVE checkout.
  //
  // The split is real. The conclusion drawn from it was not, and it was expensive:
  // native checkout is the path this store does not get paid on, so every
  // mixed-profile cart was a sale routed away from its own money.
  //
  // Measured against the live rates on 2026-08-28, the draft prices these carts
  // correctly. Every delivery profile in this shop ships Continental USA free
  // (General, Slip Cover and Made to Order alike), so a mixed cart quotes the
  // same $0 delivery whether Shopify splits it or not — a Made-to-Order sofa plus
  // a Slip Cover came to $2,212.76 either way. What the single shipping line
  // genuinely loses is the White-Glove upsell on those carts, and a $299 upsell
  // is not worth handing away a $2,000 order to collect.
  //
  // Where the draft truly cannot serve the cart, it now says so rather than
  // guessing: a mixed cart to Canada or Hawaii — where Slip Cover and Made to
  // Order have no zone at all — returns NO shipping rates, and the backend
  // refuses to build a payable draft instead of charging $0 to ship something it
  // cannot send. See autoShippingHandle in lib/draftOrder.ts.
  //
  // The buyer is told nothing here on purpose. The old copy said "taking you to
  // secure checkout", which was untrue in the one way that mattered — it was
  // taking them somewhere else — and there is now nothing to announce: they stay
  // where they are, and the shipping section shows the real rate.
  function onMixedProfile() {
    if (window.console) {
      console.info('[cc] mixed-profile cart — served here; Shopify splits the shipment at fulfilment.');
    }
  }

  function renderCartSummary(cart) {
    var list = $('[data-cart-items]');
    var subtotalCents = 0;
    // Line items are rendered server-side (Liquid) so they paint instantly with
    // the page. Only rebuild them client-side if the server didn't (fallback);
    // otherwise just tally the subtotal without touching the DOM (no image flash).
    var needBuild = list && !list.hasAttribute('data-server-rendered');
    if (needBuild) {
      list.innerHTML = '';
      cart.items.forEach(function (item) {
        subtotalCents += item.final_line_price;
        var img = item.image || (item.featured_image && item.featured_image.url) || '';
        var variant = (item.variant_title && item.variant_title !== 'Default Title') ? item.variant_title : '';
        var row = document.createElement('div');
        row.className = 'cc-line';
        row.innerHTML =
          '<div class="cc-line__media">' +
            (img ? '<img src="' + img + '" alt="">' : '') +
            '<span class="cc-line__qty">' + item.quantity + '</span>' +
          '</div>' +
          '<div class="cc-line__body">' +
            '<div class="cc-line__info">' +
              '<div class="cc-line__title">' + escapeHtml(item.product_title) + '</div>' +
              (variant ? '<div class="cc-line__variant">' + escapeHtml(variant) + '</div>' : '') +
            '</div>' +
            '<div class="cc-line__price">' + money(item.final_line_price / 100) + '</div>' +
          '</div>';
        list.appendChild(row);
      });
    }
    // Client-side subtotal (authoritative tax/shipping/total come from the backend).
    var subCents = (cart.items_subtotal_price != null && cart.items_subtotal_price > 0)
      ? cart.items_subtotal_price : subtotalCents;
    var subMoney = money(subCents / 100);
    setText('[data-summary-subtotal]', subMoney);
    // "Subtotal · N items" when the cart holds more than one unit (Shopify style).
    var subLbl = $('[data-summary-subtotal-label]');
    if (subLbl) subLbl.textContent = 'Subtotal' + (cart.item_count > 1 ? ' · ' + cart.item_count + ' items' : '');
    setText('[data-mrecap-items]', cart.item_count + (cart.item_count === 1 ? ' item' : ' items'));
    // Store credit redeemed in the cart shows IMMEDIATELY (before an address/quote),
    // mirroring the cart drawer. Coupons still need a backend quote to price, so only
    // the store-credit line + its savings appear pre-quote.
    var sc = computeStoreCredit();
    if (sc > 0) {
      setText('[data-summary-storecredit]', '−' + money(sc));
      showSel('[data-summary-storecredit-row]');
      setText('[data-summary-savings]', money(sc));
      showSel('[data-summary-savings-row]');
    } else {
      hideSel('[data-summary-storecredit-row]');
    }
    // Pre-shipping the total equals the subtotal minus any store credit
    // (Shopify shows subtotal-based total until an address is entered).
    var preMoney = money(Math.max(0, subCents / 100 - sc));
    setText('[data-summary-total]', preMoney);
    setText('[data-summary-bar-total]', preMoney);
    syncRecap();
  }

  // The draft a fresh attempt asks the server to retire, remembered across page
  // loads.
  //
  // `supersedes` used to read state.draftOrderId alone, and that lives exactly as
  // long as the page does. A buyer whose payment goes nowhere, leaves the page and
  // comes back — the most ordinary thing to do after a failed attempt — returns to
  // a null state, sends no `supersedes`, and leaves the previous draft behind. That
  // is how 2026-09-17 produced #D229 and #D230 for the same $2,153.93 sofa: both
  // open, both independently payable, and each one its own entry in the
  // abandoned-draft sweep, which mailed the same buyer twice.
  //
  // Scoped to the cart token, so an attempt only ever supersedes an earlier attempt
  // at the SAME cart. Never written in draft-link mode, where state.draftOrderId is
  // the admin's own draft and retiring it would delete the invoice we sent them.
  //
  // What this CANNOT do is decide anything: the id is a request, and supersedeDraft
  // on the server refuses to retire a draft that has an order or carries a payment
  // stamp. A stale id is therefore inert, which is why a cart-token match is guard
  // enough here and the storage can be the durable one.
  var DRAFT_MEMORY_KEY = 'cc_last_draft';

  function cartToken() {
    return (state.cart && state.cart.token) || null;
  }

  // Every storage access is wrapped: Safari private mode throws on the very first
  // read, and a checkout must not fail over a diagnostic convenience.
  function rememberDraft(id) {
    if (DRAFT || !id) return;
    var token = cartToken();
    if (!token) return;
    try {
      localStorage.setItem(DRAFT_MEMORY_KEY, JSON.stringify({ cart: token, id: String(id) }));
    } catch (e) {}
  }

  function recallDraft() {
    if (DRAFT) return null;
    var token = cartToken();
    if (!token) return null;
    try {
      var saved = JSON.parse(localStorage.getItem(DRAFT_MEMORY_KEY) || 'null');
      if (!saved || saved.cart !== token) return null;
      return saved.id || null;
    } catch (e) { return null; }
  }

  function forgetDraft() {
    try { localStorage.removeItem(DRAFT_MEMORY_KEY); } catch (e) {}
  }

  // The one place state.draftOrderId is set on the cart path — PayPal, Stripe,
  // Authorize.Net, Afterpay and Affirm all come through here — so remembering it
  // cannot be forgotten by whoever adds the next rail. The draft-link path sets
  // state.draftOrderId directly and deliberately: that id is the admin's own
  // draft, and it must never be remembered as something to retire.
  function setDraftOrderId(id) {
    state.draftOrderId = id || null;
    rememberDraft(id);
  }

  // -- 2. collect + validate the form ---------------------------------------
  function getFormData() {
    return {
      cart: {
        items: state.cart.items.map(function (i) {
          return {
            variantId: i.variant_id,
            quantity:  i.quantity,
            properties: i.properties || null
          };
        })
      },
      customer: {
        email:     val('email'),
        firstName: val('first_name'),
        lastName:  val('last_name'),
        phone:     val('phone')
      },
      shippingAddress: {
        address1: val('address1'),
        address2: val('address2'),
        city:     val('city'),
        province: val('province'),
        country:  val('country'),
        zip:      val('zip')
      },
      billingAddress: billingPayload(),
      note: (state.cart && state.cart.note) || null,
      attributes: (state.cart && state.cart.attributes) || null,
      discountCodes: state.discountCodes.slice(),
      storeCredit: computeStoreCredit(),
      shippingRateHandle: state.shippingRateHandle || null,
      // The draft this request replaces, if we were already handed one.
      //
      // Nothing here reuses a draft: every press of Place order calls create-*
      // and gets a brand-new one, deliberately, so a buyer is never charged a
      // total computed before their last edit. The cost was that each failed
      // attempt left its draft behind — open, and independently payable. Nine
      // attempts against the dead card rail on 2026-08-27 left nine live drafts
      // for the same $2,813.42 sofa, and fifteen had to be deleted by hand.
      //
      // Advisory only. The server refuses to retire a draft that could have taken
      // money — see supersedeDraft — so the worst this can do is nothing.
      //
      // Falls back to the remembered draft so that an attempt made after a reload
      // still names its predecessor; see recallDraft.
      supersedes: state.draftOrderId || recallDraft()
    };
  }

  // Billing address: when "Use shipping address as billing address" is ticked,
  // copy from shipping; otherwise read the separate billing form.
  function billingPayload() {
    var toggle = root.querySelector('[data-billing-toggle]');
    if (!toggle || toggle.checked) {
      return {
        firstName: val('first_name'), lastName: val('last_name'),
        address1: val('address1'), address2: val('address2'),
        city: val('city'), province: val('province'),
        country: val('country'), zip: val('zip'), phone: val('phone')
      };
    }
    return {
      firstName: val('billing_first_name'), lastName: val('billing_last_name'),
      address1: val('billing_address1'), address2: val('billing_address2'),
      city: val('billing_city'), province: val('billing_province'),
      country: val('billing_country'), zip: val('billing_zip'), phone: val('billing_phone')
    };
  }

  function val(name) {
    var el = root.querySelector('[name="' + name + '"]');
    return el ? el.value.trim() : '';
  }

  // A required field counts only when it is actually on screen: the billing /
  // "different billing" blocks are display:none until their method is chosen, so
  // their required fields must not block an otherwise-complete form.
  function fieldVisible(el) {
    var wrap = el.closest('.cc-fl') || el;
    return wrap.offsetParent !== null;
  }
  function markField(el, bad) {
    var wrap = el.closest('.cc-fl') || el;
    wrap.classList.toggle('cc-field--error', !!bad);
  }
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // A US or Canadian street address with no number on it is not a typo we can
  // let through. No carrier delivers to a bare street name, and AVS declines the
  // card over it, so the buyer meets "your payment was declined" for something
  // no card can fix.
  //
  // Found while investigating 2026-09-04 but NOT the cause of it — that buyer
  // closed the Afterpay popup before Afterpay had created an order, so no charge
  // was ever attempted. Her address was a second, quieter defect: `Dovedale
  // Street`, Pearland TX, on all five attempts, while every order that completed
  // that week carried a house number.
  //
  // The old pass only asked whether the box was empty, and that box was not.
  // Kept deliberately narrow — US/CA only, and the digit may sit on either line,
  // so an apartment number, a PO box or a rural route all still pass. The
  // backend refuses the same shape in addressMissingStreetNumber; this exists so
  // the buyer is told before the round trip, next to the field they must fix.
  var HAS_DIGIT = /\d/;
  var NUMBERED_COUNTRY = /^(united states|usa|us|canada|ca)$/i;
  // [street line, second line, country] for each address block on the page.
  var ADDRESS_GROUPS = [
    ['address1', 'address2', 'country'],
    ['billing_address1', 'billing_address2', 'billing_country'],
    ['pbill_address1', 'pbill_address2', 'pbill_country']
  ];
  var VALIDATION_DEFAULT = 'Please complete the required fields.';
  var validationMessage = VALIDATION_DEFAULT;

  // What the last validateForm() found, so a caller can say what is actually
  // wrong. An address missing its number is not a missing field, and telling the
  // buyer to "complete the required fields" sends them hunting for an empty box
  // that is not there.
  function validationStatus(fallback) {
    if (validationMessage !== VALIDATION_DEFAULT) return validationMessage;
    return fallback || VALIDATION_DEFAULT;
  }

  function validateForm() {
    var first = null;
    validationMessage = VALIDATION_DEFAULT;
    var fields = root.querySelectorAll('[required]');
    Array.prototype.forEach.call(fields, function (el) {
      if (!fieldVisible(el)) { markField(el, false); return; }
      var bad = !el.value || !el.value.trim();
      if (!bad && el.name === 'email' && !EMAIL_RE.test(el.value.trim())) bad = true;
      markField(el, bad);
      if (bad && !first) first = el;
    });
    // Only once the required pass is happy: an EMPTY street is a missing field,
    // and must not be reported as a missing number.
    if (!first) {
      ADDRESS_GROUPS.forEach(function (g) {
        if (first) return;
        var el = root.querySelector('[name="' + g[0] + '"]');
        if (!el || !fieldVisible(el)) return;
        if (!NUMBERED_COUNTRY.test(val(g[2]))) return;
        var lines = val(g[0]) + ' ' + val(g[1]);
        if (!lines.trim() || HAS_DIGIT.test(lines)) return;
        markField(el, true);
        validationMessage =
          'Please add the house or building number to your street address — ' +
          'we need it to deliver your order and your bank checks it against your card.';
        first = el;
      });
    }
    if (first) {
      var target = first.closest('.cc-fl') || first;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      var focusable = first.matches('select') ? (first.closest('.cc-fl').querySelector('.cc-dd__trigger') || first) : first;
      try { focusable.focus({ preventScroll: true }); } catch (e) {}
    }
    return !first;
  }

  // -- 3. create the order (draft + PayPal order) ---------------------------
  //    Called when the customer clicks "Continue to payment": produces the
  //    authoritative totals to display, then reveals the card fields.
  function createOrder() {
    // Draft-order mode: totals are LOCKED to the admin's draft. No form validation
    // or cart pricing — the backend re-reads the draft and creates a matching
    // PayPal order. Returns the same { paypalOrderId, draftOrderId, totals } shape.
    if (DRAFT) {
      setStatus('Preparing your order…', 'info');
      return fetch(ENDPOINTS.createOrderForDraft, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ draftId: DRAFT.id, token: DRAFT.token })
      })
        .then(handleJson)
        .then(function (data) {
          state.paypalOrderId = data.paypalOrderId;
          state.draftOrderId  = data.draftOrderId;
          state.totals        = data.totals;
          renderTotals(data.totals, null);
          setStatus('', null);
          return data;
        })
        .catch(function (err) {
          setStatus(friendlyError(err, 'We could not prepare your order. Please refresh, or contact us.'), 'error');
          throw err;
        });
    }
    if (!validateForm()) {
      setStatus(validationStatus(), 'error');
      return Promise.reject(new Error('validation'));
    }
    setStatus('Calculating totals…', 'info');
    return fetch(ENDPOINTS.createOrder, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(getFormData())
    })
      .then(handleJson)
      .then(function (data) {
        state.paypalOrderId = data.paypalOrderId;
        setDraftOrderId(data.draftOrderId);
        state.totals        = data.totals;
        renderTotals(data.totals, data.availableShippingRates);
        setStatus('', null);
        return data;
      })
      .catch(function (err) {
        setStatus(friendlyError(err, 'We could not calculate your order. Please check your details and try again.'), 'error');
        throw err;
      });
  }

  // The discount row's code chip, in ONE place.
  //
  // Two different functions draw this summary — renderTotals, which a pay link
  // uses, and renderQuoteTotals, which the cart checkout uses. The code-filling
  // logic lived in renderQuoteTotals only, so a pay link rendered the row and its
  // static tag icon while the code span stayed empty: an icon labelling nothing.
  // Fixing it in one renderer left the other exactly as broken, which is how this
  // survived a first repair. Hence one function, called by both.
  function setDiscountCode(codes) {
    var text = (codes || []).join(', ').toUpperCase();
    setText('[data-summary-discount-code]', text);
    // The icon is a static sibling of that span inside .cc-disc__code, so with no
    // code there is nothing for it to label — hide the whole chip. A discount with
    // no code behind it (a manual admin adjustment) lands here too. All matching
    // nodes, because the summary has a mobile mirror.
    root.querySelectorAll('[data-summary-discount-code]').forEach(function (el) {
      var wrap = el.parentNode;
      if (wrap && wrap.style) wrap.style.display = text ? '' : 'none';
    });
  }

  function renderTotals(totals, rates) {
    setText('[data-summary-subtotal]', money(totals.subtotal));
    setText('[data-summary-shipping]', parseFloat(totals.shipping) > 0 ? money(totals.shipping) : 'Free');
    // showSel/hideSel target ALL matching rows (desktop summary + mobile recap),
    // not just the first — otherwise the desktop tax/discount row can stay hidden.
    if (parseFloat(totals.tax) > 0) {
      setText('[data-summary-tax]', money(totals.tax));
      showSel('[data-summary-tax-row]');
    } else {
      hideSel('[data-summary-tax-row]');
    }
    setText('[data-summary-total]', money(totals.total));
    setText('[data-summary-bar-total]', money(totals.total));
    // Split all order discounts into coupons vs store credit (separate lines).
    var disc = parseFloat(totals.discount) || 0;
    var sc = computeStoreCredit();
    var couponDisc = Math.max(0, Math.round((disc - sc) * 100) / 100);
    if (couponDisc > 0) {
      setText('[data-summary-discount]', '−' + money(couponDisc));
      // This function showed the row and the amount and never touched the code
      // span — which on a pay link left the tag icon, a static sibling of that
      // span inside .cc-disc__code, sitting under "Order discount" labelling
      // nothing. renderQuoteTotals has always filled it; this is the OTHER
      // renderer, and the only one a pay link reaches (loadDraftMode calls it).
      // Two summaries drawn by two functions, and the fix has to live in both.
      setDiscountCode(state.discountCodes);
      showSel('[data-summary-discount-row]');
    } else {
      hideSel('[data-summary-discount-row]');
    }
    if (sc > 0) {
      setText('[data-summary-storecredit]', '−' + money(sc));
      showSel('[data-summary-storecredit-row]');
    } else {
      hideSel('[data-summary-storecredit-row]');
    }
    // Phase 2: render `rates` as selectable options + recalc on change.
  }

  // -- 4 + 5. PayPal Card Fields + pay --------------------------------------
  // How long the four empty boxes are allowed to sit there before we say something.
  // Deliberately a MESSAGE timer, not a load timeout: killing the script at 8s
  // broke the case it was meant to help — on a slow connection the SDK really does
  // arrive at 10-11s, and abandoning it there turned a late checkout into a dead
  // one. So the request always runs to completion; this only decides when the
  // customer stops being left in the dark.
  var SDK_SLOW_MS = 8000;
  var SLOW_MSG = 'The payment form is taking longer than usual to load…';

  function loadPayPalSdkOnce() {
    return new Promise(function (resolve, reject) {
      if (window.paypal && window.paypal.CardFields) return resolve(window.paypal);
      if (!CONFIG.clientId) return reject(new Error('Missing PayPal client id'));
      var s = document.createElement('script');
      // Load the Apple Pay component only on Apple/Safari (where ApplePaySession exists).
      var components = 'card-fields,buttons' + (window.ApplePaySession ? ',applepay' : '');
      s.src = 'https://www.paypal.com/sdk/js?client-id=' + encodeURIComponent(CONFIG.clientId) +
              '&components=' + components + '&intent=capture&currency=' + encodeURIComponent(CONFIG.currency);
      s.onload = function () { resolve(window.paypal); };
      s.onerror = function () { reject(new Error('Failed to load PayPal SDK')); };
      document.head.appendChild(s);
    });
  }

  // Shared by the card fields, the PayPal buttons and Apple Pay — which all ask
  // for the SDK at roughly the same moment. Caching the promise is what stops
  // them appending two or three identical <script> tags and loading the SDK more
  // than once (Safari hits this hardest: it is the only browser where the Apple
  // Pay path also asks). One retry covers a single dropped request; after that the
  // cache is cleared so a later attempt — a Place-order click — can try again.
  function loadPayPalSdk() {
    if (state.sdkPromise) return state.sdkPromise;
    state.sdkPromise = loadPayPalSdkOnce().catch(function () {
      return loadPayPalSdkOnce();
    });
    state.sdkPromise.catch(function () { state.sdkPromise = null; });
    return state.sdkPromise;
  }

  function mountCardFields() {
    return loadPayPalSdk().then(function (paypal) {
      var cardField = paypal.CardFields({
        // createOrder fires when cardField.submit() is called. We already
        // created the order at "Continue to payment", so return its id.
        createOrder: function () {
          if (state.paypalOrderId) return Promise.resolve(state.paypalOrderId);
          return createOrder().then(function () { return state.paypalOrderId; });
        },
        onApprove: function (data) { return captureOrder(data.orderID || state.paypalOrderId); },
        onError: function (err) {
          // A rejected onApprove reaches PayPal's onError too. If the capture stage
          // already reported it, leave that message (and the locked button) alone —
          // and do not log it either: a capture that failed is already a fact on the
          // server, and a second line for it would only make the log lie about how
          // often a rail dies unseen.
          if (err && err.ccHandled) return;
          reportRailEvent('card', 'error', state.draftOrderId, err && err.message);
          state.submitting = false;
          togglePayButton(false);
          setStatus(friendlyError(err, 'Your payment could not be processed. Please check your card details.'), 'error');
        },
        // Let PayPal's own input BE the styled field — border, radius, placeholder
        // and vertical centring are all handled natively by PayPal (reliable across
        // browsers). We only theme the colours. Our wrapper stays border-less so
        // there's a single frame.
        style: {
          input: {
            'font-size': '14px',
            'font-family': 'Inter, -apple-system, sans-serif',
            color: '#1a1a1a',
            'background-color': '#ffffff',
            border: '1px solid #d9d9d9',
            'border-radius': '8px',
            // Override PayPal's default 1.5rem (24px) line-height with `normal` so
            // the text/placeholder sits centred (verified in DevTools). `normal` is
            // safe — unlike a fixed px value, it doesn't collapse/hide the line.
            'line-height': 'normal',
            // Vertical padding sets the field height (the input sizes to its own
            // content, not the container). 14px top/bottom for a slightly taller field.
            padding: '14px 12px'
          },
          ':focus': { border: '1px solid #1a1a1a', 'box-shadow': '0 0 0 1px #1a1a1a' },
          '::placeholder': { color: '#8a8a8a' },
          '.invalid': { border: '1px solid #d82c0d', 'box-shadow': '0 0 0 1px #d82c0d' }
        }
      });

      if (!cardField.isEligible()) {
        setStatus('Card payments are not available right now. Please try another method.', 'error');
        // Other rails (PayPal, Apple Pay, Afterpay, Affirm) are unaffected, so the
        // button must not stay locked on their behalf.
        setPayReady(true);
        return;
      }

      // PayPal's native placeholders (its own input renders + centres them).
      cardField.NumberField({ placeholder: 'Card number' }).render('[data-pp-number]');
      cardField.ExpiryField({ placeholder: 'Expiration date (MM / YY)' }).render('[data-pp-expiry]');
      cardField.CVVField({ placeholder: 'Security code' }).render('[data-pp-cvv]');
      cardField.NameField({ placeholder: 'Name on card' }).render('[data-pp-name]');
      state.cardField = cardField;
      show($('[data-payment-section]'));
      setPayReady(true);
    });
  }

  // -- PayPal Smart Buttons --------------------------------------------------
  // Rendered in the PayPal payment method AND the top "Express checkout" slot.
  // The customer fills the form first (authoritative totals), so PayPal only
  // authorises the payment. createOrder builds a FRESH order on every popup so
  // the amount always matches the current totals (never a stale one), then the
  // shared captureOrder finalises it — same backend path as card + Apple Pay.
  function paypalButtonsConfig() {
    return {
      fundingSource: window.paypal.FUNDING.PAYPAL,
      style: { layout: 'horizontal', color: 'gold', shape: 'rect', label: 'paypal', height: 49, tagline: false },
      onClick: function (data, actions) {
        // The express button is rendered before the cart arrives, so it can be
        // pressed while there is still nothing to price.
        if (!state.draftMode && !state.cartLoaded) {
          setStatus('We could not load your cart. Please refresh the page — your items are safe.', 'error');
          return actions.reject();
        }
        if (!validateForm()) {
          setStatus(validationStatus('Please complete the required fields above before paying with PayPal.'), 'error');
          var firstErr = root.querySelector('.cc-field--error, .cc-fl--error, [aria-invalid="true"]');
          if (firstErr && firstErr.scrollIntoView) firstErr.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return actions.reject();
        }
        // Cleared before the attempt, not after it — the same discipline the
        // Afterpay rail uses, and for the same reason: a cancel that arrives before
        // this attempt has a draft must report none rather than the previous one.
        state.ppDraftId = null;
        state.ppInFlight = true;
        return actions.resolve();
      },
      createOrder: function () {
        return createOrder().then(function () {
          state.ppDraftId = state.draftOrderId;
          return state.paypalOrderId;
        });
      },
      onApprove: function (data) {
        setStatus('Finalising your order…', 'info');
        return captureOrder(data.orderID || state.paypalOrderId)
          .then(function () { state.ppInFlight = false; });
      },
      // Both of these leave an open draft behind and say nothing to the server, so
      // both report. Until now only Afterpay did, and the cost was exact: on
      // 2026-09-17 a buyer left #D229 and #D230 open for the same $2,153.93 sofa and
      // there was no way to tell a PayPal decline from a closed window — the two
      // answers that call for completely different responses.
      onCancel: function () {
        state.ppInFlight = false;
        reportRailEvent('paypal', 'cancelled', state.ppDraftId);
        setStatus('', '');
      },
      onError: function (err) {
        state.ppInFlight = false;
        // Same as the card fields: a capture failure surfaces here as well, already
        // reported. "Please try again" over a held payment is how you get paid twice.
        if (err && err.ccHandled) return;
        reportRailEvent('paypal', 'error', state.ppDraftId, err && err.message);
        setStatus(friendlyError(err, 'PayPal could not process your payment. Please try again.'), 'error');
      }
    };
  }

  // Render one PayPal button into `sel`. PayPal measures the host width at render
  // time, so the host MUST be visible — callers render the bottom button lazily,
  // only once PayPal is selected (its slot is otherwise hidden).
  function mountPayPalButton(sel) {
    if (!window.paypal || !window.paypal.Buttons) return;
    var host = root.querySelector(sel);
    if (!host || host.getAttribute('data-pp-mounted')) return;
    try {
      var btns = window.paypal.Buttons(paypalButtonsConfig());
      if (btns.isEligible && !btns.isEligible()) return;
      btns.render(host);
      host.setAttribute('data-pp-mounted', '1');
    } catch (e) { /* leave the fallback note visible */ }
  }

  // Only the always-visible top "Express checkout" button is rendered at init.
  // The bottom button is deferred to syncPayCta (rendered when its slot shows).
  function mountPayPalButtons() { mountPayPalButton('[data-express-paypal]'); }

  // Every payment rail in one call, and deliberately NOT chained behind the cart.
  //
  // These used to hang off loadCart().then(...), which meant a slow or refused
  // /cart.js took the whole payment UI down with it: measured on the live page, a
  // 12s cart delayed the card fields to 12.9s and a 429 left them empty for ever
  // under "Something went wrong loading checkout". None of this needs the cart —
  // PayPal is asked for an order id at pay time, not at mount time — so it now
  // starts immediately and the cart loads alongside it.
  function mountPaymentRails() {
    // Level A (design preview) runs without a client-id: show the layout, no error,
    // and leave the button alone rather than locking it for a mount that never runs.
    if (!CONFIG.clientId && !isStripeCard() && !isAuthNetCard()) { setPayReady(true); return Promise.resolve(); }
    setPayReady(false);
    state.railError = false;
    var slow = setTimeout(function () {
      setStatus(SLOW_MSG, 'info');
    }, SDK_SLOW_MS);

    // PayPal's own Apple Pay integration only runs on PayPal's own gateway. On
    // Authorize.Net there is no Apple Pay to offer at all, and on Stripe it was a
    // tab inside the Payment Element — either way PayPal stays out of the way.
    // The PayPal WALLET row is a separate rail that keeps working on every
    // gateway; its SDK is fetched on demand, and its failure must not take the
    // card form down with it.
    // The buyer is waiting for ONE thing: a card form they can type into. Retract
    // the "taking longer" note the moment that exists, rather than when the last
    // secondary rail has finished — the note used to outlive the form it was
    // apologising for.
    function cardReady() {
      clearTimeout(slow);
      clearStatusIf(SLOW_MSG);
    }

    var cardMount;
    if (isAuthNetCard()) {
      // Accept.js is ~30 KB and needs no key at mount time, so nothing here is
      // deferred. The PayPal SDK is NOT fetched — 397 KB for a rail almost
      // nobody on this page will pick; syncPayCta() gets it the moment someone
      // actually selects the PayPal row.
      cardMount = mountAuthNetCard().then(cardReady);
    } else if (isStripeCard()) {
      // The PayPal SDK is NOT fetched here. It is 397 KB on top of Stripe.js's
      // 1 MB — 1.4 MB of payment code on every visit, more than the whole rest
      // of the page, when nearly every buyer uses one rail. With the express row
      // hidden on this gateway, nothing on first paint needs it; syncPayCta()
      // fetches it the moment someone actually picks the PayPal row.
      cardMount = mountStripeCard().then(cardReady);
    } else {
      // Hand the four card slots back to PayPal. Harmless when they were never
      // taken — and necessary when ?cc_gateway flipped the page at runtime.
      showCardSlots('paypal');
      initApplePay();
      cardMount = mountCardFields().then(cardReady);
    }

    return cardMount
      .then(mountPayPalButtons)
      .then(function () {
        clearTimeout(slow);
        // Retract the "taking longer" note — and ONLY that. Anything else on screen
        // (an empty cart, a quote error) belongs to someone else.
        clearStatusIf(SLOW_MSG);
      })
      .catch(function (err) {
        clearTimeout(slow);
        if (window.console) console.error('[cc] payment rails failed to mount:', err);
        // A buyer who never saw a card form leaves no draft and no gateway
        // transaction, so this failure is invisible from every other system.
        reportRailEvent('card', 'mount-failed', null, err && err.message);
        state.railError = true;
        setStatus(
          friendlyError(err, 'We could not load the payment form. Check your connection and tap Place order to try again.'),
          'error'
        );
        // Unlocked on purpose: the click is what retries (see pay()), and the
        // rails that never needed the SDK still work.
        setPayReady(true);
      });
  }

  // The shared "Pay now" button drives card + Apple Pay. When PayPal is selected
  // it is replaced (in the same slot) by the PayPal button, rendered on demand.
  function syncPayCta() {
    var method = selectedPayMethod();
    var isPaypal = (method === 'paypal');
    // Stripe's Apple Pay row carries Apple's own button, so the shared CTA steps
    // aside there exactly as it does for PayPal.
    var stripeApplePay = isStripeCard() && method === 'applepay';
    var payBtn = $('[data-pay-button]');
    var ppPay = $('[data-paypal-pay]');
    var apPay = $('[data-applepay-pay]');
    if (payBtn) payBtn.style.display = (isPaypal || stripeApplePay) ? 'none' : '';
    // Only once the button is really in there: an empty 49px box where Place
    // order should be reads as a broken checkout.
    if (apPay) apPay.hidden = !(stripeApplePay && apPay.children.length > 0);
    if (ppPay) {
      ppPay.hidden = !isPaypal;
      if (isPaypal) {
        if (!isPayPalCard() && CONFIG.clientId && !window.paypal) {
          // First moment we know the buyer wants PayPal — fetch it now.
          setStatus('Loading PayPal…', 'info');
          loadPayPalSdk().then(function () {
            clearStatusIf('Loading PayPal…');
            mountPayPalButton('[data-paypal-pay]');
          }).catch(function (e) {
            clearStatusIf('Loading PayPal…');
            setStatus(friendlyError(e, 'PayPal could not load. Please choose another method.'), 'error');
          });
        } else {
          mountPayPalButton('[data-paypal-pay]'); // already loaded → safe to render
        }
      }
    }
    // Warm up afterpay.js the moment the customer picks Afterpay so it's ready
    // for the Pay-now click (loaded, but never initialized until then).
    // Only the legacy rail needs Afterpay's own SDK; the Stripe path is a redirect.
    if (method === 'afterpay' && !isStripeCard()) preloadAfterpay();
    if (method === 'affirm') loadAffirm(); // warm up affirm.js on intent
  }

  // -- Apple Pay via PayPal --------------------------------------------------
  // Apple Pay only exists in Safari (macOS/iOS). The option renders its button
  // only when the device can pay AND PayPal reports the merchant eligible.
  // Go-live also needs the domain registered with PayPal for Apple Pay +
  // /.well-known/apple-developer-merchantid-domain-association served.
  function initApplePay() {
    var method = root.querySelector('[data-applepay-method]');
    var container = root.querySelector('[data-applepay-container]');
    var note = root.querySelector('[data-applepay-note]');
    if (!method || !container) return;
    if (!window.ApplePaySession || !ApplePaySession.canMakePayments || !ApplePaySession.canMakePayments()) {
      if (note) note.textContent = 'Apple Pay is available in Safari on a supported Apple device.';
      return;
    }
    loadPayPalSdk().then(function (paypal) {
      if (!paypal.Applepay) return;
      var applepay = paypal.Applepay();
      return applepay.config().then(function (cfg) {
        if (!cfg || !cfg.isEligible) {
          if (note) note.textContent = 'Apple Pay is not available for this store yet.';
          return;
        }
        // Eligible: route Apple Pay through the shared "Pay now" button (no separate
        // Apple Pay button). Stash the SDK objects for pay() to use.
        state.applepay = applepay;
        state.applepayCfg = cfg;
        if (container) { container.hidden = true; container.innerHTML = ''; }
        if (note) { note.hidden = false; note.textContent = 'You’ll confirm your payment with Apple Pay after selecting Pay now.'; }
        mountExpressApplePay(); // now eligible → show the top Express Apple Pay button
      });
    }).catch(function (e) {
      if (window.console) console.warn('[cc] Apple Pay init skipped:', e);
    });
  }

  // Render Apple's <apple-pay-button> in the top Express slot (next to PayPal).
  // Only when Apple Pay is eligible (state.applepay set by initApplePay). Clicking
  // it runs the same startApplePay flow as the "Pay now" button (Model 1: the form
  // is filled first, so the total is authoritative before the sheet opens).
  function mountExpressApplePay() {
    var host = root.querySelector('[data-express-applepay]');
    if (!host || !state.applepay || host.getAttribute('data-ap-mounted')) return;
    loadApplePayButtonSdk().then(function () {
      if (!window.ApplePaySession) return;
      host.innerHTML = '<apple-pay-button buttonstyle="black" type="plain" locale="en-US"></apple-pay-button>';
      var btn = host.querySelector('apple-pay-button');
      if (btn) btn.addEventListener('click', function () {
        startApplePay(state.applepay, state.applepayCfg);
      });
      host.hidden = false;
      host.setAttribute('data-ap-mounted', '1');
    });
  }

  function loadApplePayButtonSdk() {
    return new Promise(function (resolve) {
      if (window.customElements && customElements.get('apple-pay-button')) return resolve();
      var s = document.createElement('script');
      s.src = 'https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js';
      s.crossOrigin = 'anonymous';
      s.onload = function () { resolve(); };
      s.onerror = function () { resolve(); };
      document.head.appendChild(s);
    });
  }

  // Reuses the same create-order → capture-order backend flow as the card path;
  // Apple Pay only adds merchant validation + confirmOrder(token) in between.
  function startApplePay(applepay, cfg) {
    if (!validateForm()) { setStatus(validationStatus(), 'error'); return; }
    var total = state.totals && state.totals.total;
    if (!total) { setStatus('Enter your address to calculate the total before paying.', 'error'); return; }
    var paymentRequest = {
      countryCode: cfg.countryCode || 'US',
      currencyCode: CONFIG.currency || 'USD',
      merchantCapabilities: cfg.merchantCapabilities,
      supportedNetworks: cfg.supportedNetworks,
      requiredBillingContactFields: ['name', 'postalAddress'],
      total: { label: 'The Panel Hub', amount: String(total), type: 'final' }
    };
    var session = new ApplePaySession(4, paymentRequest);

    session.onvalidatemerchant = function (event) {
      applepay.validateMerchant({ validationUrl: event.validationURL })
        .then(function (payload) { session.completeMerchantValidation(payload.merchantSession); })
        .catch(function (err) {
          if (window.console) console.error('[cc] Apple Pay merchant validation failed:', err);
          session.abort();
        });
    };

    session.onpaymentauthorized = function (event) {
      createOrder()
        .then(function () {
          return applepay.confirmOrder({
            orderId: state.paypalOrderId,
            token: event.payment.token,
            billingContact: event.payment.billingContact
          });
        })
        .then(function () { return captureOrder(state.paypalOrderId); })
        .then(function () { session.completePayment(ApplePaySession.STATUS_SUCCESS); })
        .catch(function (err) {
          if (window.console) console.error('[cc] Apple Pay payment failed:', err);
          // The sheet must close either way, so this runs before the guard below.
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          // Already reported by the capture stage — do not replace it with a prompt
          // to try another method when the money has in fact already been taken.
          if (err && err.ccHandled) return;
          setStatus(friendlyError(err, 'Apple Pay could not be completed. Please try another method.'), 'error');
        });
    };

    session.oncancel = function () {};
    session.begin();
  }

  function selectedPayMethod() {
    var el = root.querySelector('[name="paymethod"]:checked');
    return el ? el.value : 'card';
  }

  function pay() {
    if (!validateForm()) { setStatus(validationStatus(), 'error'); return; }
    if (state.submitting) return;
    // The rails no longer wait for the cart, so the cart can still be missing when
    // the button is pressed. Every pay path below prices from state.cart, and
    // "nothing happens" is the worst possible answer here.
    if (!state.draftMode && !state.cartLoaded) {
      setStatus('We could not load your cart. Please refresh the page — your items are safe.', 'error');
      return;
    }
    var method = selectedPayMethod();
    // Apple Pay runs through the shared "Pay now" button (no separate Apple Pay
    // button). session.begin() below stays inside this click's user gesture.
    if (method === 'applepay') {
      // Stripe's Apple Pay has its own button in its own row — Apple does not
      // accept a substitute trigger — so the shared button is hidden while that
      // row is selected and this only fires if something went wrong.
      if (isStripeCard()) {
        setStatus('Use the Apple Pay button above to complete your purchase.', 'info');
        return;
      }
      if (state.applepay && state.applepayCfg) startApplePay(state.applepay, state.applepayCfg);
      else setStatus('Apple Pay is available in Safari on a supported Apple device.', 'error');
      return;
    }
    // Afterpay runs through the shared "Pay now" button. AfterPay.open() below MUST
    // stay inside this click's user gesture (else the popup is blocked).
    // On Stripe this is a full-page redirect, which needs no popup and so cannot
    // be blocked; the legacy direct rail opens AfterPay.open() inside this click's
    // gesture. Same shape as the Affirm branch below it.
    if (method === 'afterpay') {
      if (isStripeCard()) payWithAfterpay();
      else startAfterpay();
      return;
    }
    // Affirm also runs through "Pay now". On Stripe it is a full-page redirect,
    // which needs no popup and so cannot be blocked; the legacy affirm.js path
    // opens a modal inside this click's gesture.
    if (method === 'affirm') {
      if (isStripeCard()) payWithAffirm();
      else startAffirm();
      return;
    }
    if (method !== 'card') return; // PayPal uses its own button
    if (isAuthNetCard()) { payWithAuthNetCard(); return; }
    if (isStripeCard()) { payWithStripeCard(); return; }
    // Card fields never came up. Say so, and use the click as the retry — this is
    // the one moment we know the customer is still here and wants to pay.
    if (!state.cardField) {
      setStatus('The card form is still loading — one moment…', 'info');
      mountPaymentRails().then(function () {
        if (state.cardField) setStatus('Card details are ready — tap Place order to pay.', 'info');
      });
      return;
    }
    state.submitting = true;
    togglePayButton(true);
    setStatus('Processing payment…', 'info');
    state.cardField.submit()
      .catch(function (err) {
        // onApprove -> captureOrder rethrows, so a capture failure lands here too.
        // It has already been reported (and may have deliberately locked the pay
        // button); saying "check your card details" on top of that would be both
        // wrong and an invitation to pay twice.
        if (err && err.ccHandled) return;
        state.submitting = false;
        togglePayButton(false);
        setStatus(friendlyError(err, 'Payment failed. Please check your card details and try again.'), 'error');
      });
  }

  // Every capture endpoint is idempotent: it returns the existing order when the
  // draft already has one. That is what makes a single retry safe, and it is the
  // difference between a lost answer and a bad one.
  //
  // Learned on the Stripe rail: two live orders were built correctly while the
  // browser was told "we took your payment but hit a snag — do not pay again",
  // because the request died without a reply. The same shape exists on every
  // rail, pay-by-link included, since PayPal and Afterpay have webhooks that can
  // equally finish a draft while the browser is still waiting.
  //
  // A `held` payment is never retried: that is a real answer, not a lost one,
  // and onCaptureFailed has already locked the button on purpose.
  function retryCaptureOnce(err, isRetry, again) {
    if (isRetry || (err && err.ccHandled)) return null;
    // One more try is worth it when the capture failed with no verdict — a lost
    // reply, a cold function, a timeout. Capture is idempotent on every rail
    // (the draft's own order is checked first), so a second call either finds
    // the order already built or repeats the same answer.
    //
    // But a verdict the backend actually REACHED will not change in 1.8 seconds.
    // `retryable` means it proved no money moved; `held` means it proved money
    // did. Retrying either one only holds the buyer on a spinner before showing
    // them a message that was ready immediately — and on `held`, those are the
    // seconds in which someone decides to press pay again.
    var body = (err && err.body) || null;
    if (body && (body.retryable || body.held)) return null;
    return new Promise(function (resolve) { setTimeout(resolve, 1800); }).then(again);
  }

  function captureOrder(paypalOrderId, isRetry) {
    return fetch(ENDPOINTS.captureOrder, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ paypalOrderId: paypalOrderId, draftOrderId: state.draftOrderId })
    })
      .then(handleJson)
      .then(function (data) {
        if (!data.success) throw new Error('capture-not-successful');
        // Authoritative payment method from the PayPal capture (brand + last4).
        state.paymentMethod = data.paymentMethod || null;
        return onOrderComplete(data.order);
      })
      .catch(function (err) {
        var retry = retryCaptureOnce(err, isRetry, function () {
          setStatus('Confirming your order…', 'info');
          return captureOrder(paypalOrderId, true);
        });
        if (retry) return retry;
        onCaptureFailed(err, 'We captured your payment but hit a snag finalising the order. Please contact us — do not pay again.');
        throw err;
      });
  }

  // -- Stripe (card · Apple Pay · Affirm) -----------------------------------
  // Three rows, three flows, one PaymentIntent behind all of them:
  //
  //   Credit card  split card elements mounted into the SAME four slots PayPal
  //                uses, so the form does not move a pixel between gateways.
  //   Apple Pay    its own row and Apple's own button — Apple will not accept a
  //                substitute. Revealed only once the device says it can pay.
  //   Affirm       no fields at all: a confirm that leaves the site and returns
  //                to return_url.
  //
  // The intent is created at pay time in every case, so none of this needs an
  // amount at mount time or a deferred-Element submit step.
  function isStripeCard() { return CONFIG.cardGateway === 'stripe'; }

  function loadStripeSdk() {
    return new Promise(function (resolve, reject) {
      if (window.Stripe) return resolve(window.Stripe);
      var el = document.createElement('script');
      el.src = 'https://js.stripe.com/v3/';
      el.onload = function () {
        if (window.Stripe) resolve(window.Stripe);
        else reject(new Error('Stripe.js loaded but did not initialise'));
      };
      el.onerror = function () { reject(new Error('Failed to load Stripe.js')); };
      document.head.appendChild(el);
    });
  }

  // Matched to the PayPal field styling above — same size, family and colours.
  // The border is NOT here: a Stripe element is transparent, so .cc-st-field
  // carries the frame (PayPal draws its own inside a border-less host).
  function stripeFieldStyle() {
    return {
      base: {
        fontSize: '14px',
        fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        color: '#1a1a1a',
        lineHeight: 'normal',
        '::placeholder': { color: '#8a8a8a' }
      },
      // Stripe reddens the text on an invalid card; the frame says it instead, so
      // this stays the normal ink and .is-invalid does the work.
      invalid: { color: '#1a1a1a' }
    };
  }

  // PayPal's hosts, Stripe's hosts and Authorize.Net's own inputs all share the
  // same four slots. Exactly one set is ever on screen, so the card form keeps
  // its layout whichever gateway is live — and hiding the other two is what makes
  // the runtime ?cc_gateway switch safe, since all three are always in the markup.
  var CARD_SLOTS = {
    paypal:  ['[data-pp-number]', '[data-pp-expiry]', '[data-pp-cvv]', '[data-pp-name]'],
    stripe:  ['[data-st-number]', '[data-st-expiry]', '[data-st-cvc]', '[data-st-name]'],
    authnet: ['[data-an-number]', '[data-an-expiry]', '[data-an-cvc]', '[data-an-name]']
  };

  function showCardSlots(which) {
    Object.keys(CARD_SLOTS).forEach(function (key) {
      CARD_SLOTS[key].forEach(function (sel) {
        var el = root.querySelector(sel);
        if (el) el.hidden = (key !== which);
      });
    });
  }

  function bindStripeField(element, selector) {
    var host = root.querySelector(selector);
    if (!host) return;
    element.mount(host);
    element.on('focus', function () { host.classList.add('is-focus'); });
    element.on('blur', function () { host.classList.remove('is-focus'); });
    element.on('change', function (ev) {
      if (ev && ev.error) host.classList.add('is-invalid');
      else host.classList.remove('is-invalid');
    });
  }

  function mountStripeCard() {
    // Loudly, not silently: a resolved promise here would skip the setPayReady
    // handover below and leave the button reading "Loading payment…" for ever.
    if (!root.querySelector('[data-st-number]')) {
      return Promise.reject(new Error('Stripe card slots are missing from the page'));
    }
    // The publishable key comes from the backend, not a theme setting, so it is
    // always from the same account and mode as the secret key that will create
    // the intent. A theme setting can drift; this cannot.
    // Both at once. The key and the SDK do not depend on each other, and running
    // them in series put a round trip in front of a script download for no
    // reason — the buyer waits for the sum instead of the slower one.
    return Promise.all([
      fetch(ENDPOINTS.stripeConfig, { headers: { Accept: 'application/json' } }).then(handleJson),
      loadStripeSdk()
    ])
      .then(function (both) {
        var cfg = both[0], Stripe = both[1];
        if (!cfg || !cfg.publishableKey) throw new Error('stripe-not-configured');
        return (function () {
          state.stripe = Stripe(cfg.publishableKey);
          state.stripeElements = state.stripe.elements();
          var style = stripeFieldStyle();

          state.stripeCardNumber = state.stripeElements.create('cardNumber', {
            style: style,
            placeholder: 'Card number',
            // The slot already carries the design's own lock icon, absolutely
            // positioned at right:12px. Stripe's brand icon sits in the same
            // place and the two would overlap.
            showIcon: false,
            // Kills the green "Autofill" pill Stripe injects into the card field.
            // That is Link, which this account has enabled — a second checkout
            // brand inside our own card form, offering a flow this rail does not
            // handle. The field is ours; it should carry nothing we did not put
            // there.
            disableLink: true
          });
          bindStripeField(state.stripeCardNumber, '[data-st-number]');
          bindStripeField(
            state.stripeElements.create('cardExpiry', { style: style, placeholder: 'Expiration date (MM / YY)' }),
            '[data-st-expiry]'
          );
          bindStripeField(
            state.stripeElements.create('cardCvc', { style: style, placeholder: 'Security code' }),
            '[data-st-cvc]'
          );

          showCardSlots('stripe');
          initStripeApplePay();
          affirmRowSync(); afterpayRowSync();
          show($('[data-payment-section]'));
          setPayReady(true);
        })();
      });
  }

  // ---- Apple Pay ----------------------------------------------------------
  // Revealed only once the device confirms it can pay. An Apple Pay row that does
  // nothing is worse than no row — and on a plain http theme-dev server
  // canMakePayment() always answers no, however correct the setup is.
  function initStripeApplePay() {
    var row = root.querySelector('[data-applepay-method]');
    // The button goes where Place order is, not inside the accordion: Apple
    // supplies it and it IS the pay action, so two clickable-looking places is
    // one too many. The accordion keeps only its explanatory line.
    var host = root.querySelector('[data-applepay-pay]');
    if (!row || !host || !state.stripe) return;
    // Built once, and never before there is a real amount. Stripe refuses a
    // paymentRequest whose total is zero, and the rails mount immediately —
    // deliberately, so the card form does not wait for the cart — which meant
    // this ran with amount 0 and Apple Pay died silently every time. There is no
    // error to see: canMakePayment() simply never resolves true.
    if (state.stripePaymentRequest) return;
    if (stripeAmountCents() <= 0) return;

    var pr = state.stripe.paymentRequest({
      country: 'US',
      currency: (CONFIG.currency || 'USD').toLowerCase(),
      total: { label: 'Sofatica', amount: stripeAmountCents() },
      requestPayerName: true,
      requestPayerEmail: true
    });

    pr.on('paymentmethod', function (ev) {
      // The sheet is already open and holding a real payment method, so a form
      // that is not filled in has to fail the sheet rather than the payment.
      if (!validateForm()) {
        ev.complete('fail');
        setStatus(validationStatus('Please complete the required fields above before paying with Apple Pay.'), 'error');
        return;
      }
      state.submitting = true;
      setStatus('Processing payment…', 'info');
      createStripeIntent()
        .then(function (data) {
          // handleActions:false so the sheet can be dismissed before any 3-D
          // Secure step; leaving it open through a challenge strands the buyer.
          return state.stripe.confirmCardPayment(
            data.clientSecret,
            { payment_method: ev.paymentMethod.id },
            { handleActions: false }
          );
        })
        .then(function (result) {
          if (result && result.error) { ev.complete('fail'); throw stripeError(result.error); }
          ev.complete('success');
          var pi = result.paymentIntent;
          if (pi.status === 'requires_action') {
            return state.stripe.confirmCardPayment(pi.client_secret).then(function (r2) {
              if (r2 && r2.error) throw stripeError(r2.error);
              return captureStripe(r2.paymentIntent.id);
            });
          }
          return captureStripe(pi.id);
        })
        .catch(handleStripeFailure);
    });

    state.stripePaymentRequest = pr;

    pr.canMakePayment().then(function (result) {
      // Only Apple Pay belongs in this row. A device offering Google Pay or a
      // saved card would otherwise light up a row labelled Apple Pay.
      if (!result || !result.applePay) {
        // Says why, because every reason looks identical from the page: no card
        // in Wallet, not Safari, an unregistered domain, or plain http.
        if (window.console) {
          console.info('[cc] Apple Pay unavailable on this device/browser.', result || '(canMakePayment returned null)');
        }
        if (forceRowsVisible()) {
          // Layout preview: show the row, but no button — this device or this
          // origin cannot actually pay, and a dead Apple Pay button would be a
          // lie rather than a preview.
          row.hidden = false;
          var devNote = root.querySelector('[data-applepay-note]');
          if (devNote) devNote.textContent = 'Layout preview only — Apple Pay needs HTTPS and a registered domain.';
          syncPayCta();
        }
        return;
      }
      // Unhidden BEFORE mounting — Stripe cannot size a button inside a
      // display:none box — and wrapped, because a throw here must not take the
      // row with it. The row is the buyer's only sign the option exists.
      try {
        host.hidden = false;
        state.stripeElements.create('paymentRequestButton', {
          paymentRequest: pr,
          // 44px inside a 49px slot. Stripe exposes no way to scale the Apple
          // Pay logo independently — it follows the button height — so this is
          // the only lever for a smaller mark. The slot stays 49px so the row
          // still lines up with Place order and the PayPal button, and its
          // border-radius does the rounding since Stripe offers none either.
          // 40px inside a 49px black box — see .cc-applepay-pay. The row keeps
          // Place order's height while the mark itself reads smaller.
          style: { paymentRequestButton: { type: 'buy', theme: 'dark', height: '40px' } }
        }).mount(host);
      } catch (e) {
        host.hidden = true;
        if (window.console) console.warn('[cc] Apple Pay button could not mount:', e);
      }
      row.hidden = false;
      // No copy override here. The line is written once in the markup; two
      // sources for one sentence is how the row ended up saying one thing in the
      // HTML and another once the script ran.
      syncPayCta();
    });
  }

  // Reveals the rows that would otherwise wait on Stripe.js, so the payment list
  // can be reviewed with no account at all. Only ever under ?cc_rows=all on
  // localhost, and it never mounts a button — initStripeApplePay still decides
  // whether this device can genuinely pay, and overwrites the note when it can.
  function previewStripeRows() {
    if (!forceRowsVisible()) return;
    var apRow = root.querySelector('[data-applepay-method]');
    if (apRow) {
      apRow.hidden = false;
      var note = root.querySelector('[data-applepay-note]');
      if (note) note.textContent = 'Layout preview only — Apple Pay needs HTTPS and a registered domain.';
    }
    affirmRowSync(); afterpayRowSync();
    syncPayCta();
  }

  // ---- Affirm -------------------------------------------------------------
  // Affirm's US floor through Stripe, confirmed against the account's own
  // settings: transactions run USD 35.00 – 30,000.00. It was 5000 here, a guess
  // at $50 that hid the row on every order between $35 and $49.99 — Affirm
  // offers Pay in 30 there, so the option was being withheld for no reason.
  //
  // Stripe still leaves Affirm off an intent it cannot finance; this only stops
  // the buyer meeting that refusal at the last click.
  var AFFIRM_MIN_CENTS = 3500;

  function affirmRowSync() {
    var row = root.querySelector('[data-affirm-method]');
    if (!row || !isStripeCard()) return;

    // Deliberately NOT gated on Stripe.js having loaded. Affirm needs no field to
    // render, so making its row wait for the SDK only meant the payment list looked
    // wrong — or, with no key configured at all, permanently missing an option the
    // store actually offers.
    //
    // Hidden only when the total is KNOWN to be under the floor. An empty or
    // not-yet-priced cart is not evidence of ineligibility, and hiding on it is
    // what made the row invisible while the layout was being reviewed.
    var cents = stripeAmountCents();
    var belowFloor = cents > 0 && cents < AFFIRM_MIN_CENTS;
    if (forceRowsVisible()) belowFloor = false;
    row.hidden = belowFloor;
    if (!belowFloor) return;
    // Never leave a hidden row selected — the pay button would do nothing.
    var affirmRadio = root.querySelector('[name="paymethod"][value="affirm"]');
    if (affirmRadio && affirmRadio.checked) {
      var cardRadio = root.querySelector('[name="paymethod"][value="card"]');
      if (cardRadio) { cardRadio.checked = true; syncPayCta(); }
    }
  }

  // ---- Afterpay, through Stripe -------------------------------------------
  // Afterpay used to run on its own rail: its own merchant account, its own
  // credentials, its own client and two of its own endpoints, settling into
  // separate books. In the two months to 2026-08-28 that rail took exactly ONE
  // order — SFT8251447, $1,967.81 — against 18 on PayPal and 8 on our Stripe
  // rail. Stripe already had `afterpay_clearpay` enabled the whole time.
  //
  // So it now rides the Stripe path, which means one set of books, one dispute
  // and refund path, and — the part that matters most — the same redirect-then-
  // confirm capture as Affirm, hardened on 2026-08-28. The old rail's separate
  // capture endpoint had to be reasoned about on its own every time.
  //
  // THE CEILING IS REAL AND LOWER THAN AFFIRM'S. Probed against this account on
  // 2026-08-28: Stripe offers afterpay_clearpay at $4,000 and NOT at $4,100.
  // Above that the buyer still has Affirm, which runs to $30,000 — so nothing is
  // left without instalments, it just changes provider. 2 of 51 recent paid
  // orders sat above the line.
  var AFTERPAY_MAX_CENTS = 400000;

  function afterpayRowSync() {
    var row = root.querySelector('[data-afterpay-method]');
    if (!row || !isStripeCard()) return;
    // Same rule as affirmRowSync, mirrored: hide only when the total is KNOWN to
    // be out of range. A cart that has not been priced yet is not evidence of
    // anything, and hiding on it makes the payment list look broken.
    var cents = stripeAmountCents();
    var overCeiling = cents > AFTERPAY_MAX_CENTS;
    if (forceRowsVisible()) overCeiling = false;
    row.hidden = overCeiling;
    if (!overCeiling) return;
    // Never leave a hidden row selected — the pay button would do nothing.
    var apRadio = root.querySelector('[name="paymethod"][value="afterpay"]');
    if (apRadio && apRadio.checked) {
      var cardRadio2 = root.querySelector('[name="paymethod"][value="card"]');
      if (cardRadio2) { cardRadio2.checked = true; syncPayCta(); }
    }
  }

  function payWithAfterpay() {
    if (!state.stripe) { setStatus('Afterpay is still loading — one moment…', 'info'); return; }
    state.submitting = true;
    togglePayButton(true);
    setStatus('Preparing Afterpay…', 'info');
    createStripeIntent()
      .then(function (data) {
        // Leaves the site, exactly like Affirm; resumeStripeRedirect picks the
        // buyer up on the way back. That function reads `payment_intent` from the
        // query string, which Stripe appends for every redirect method, so it
        // needed no change to cover this one.
        //
        // billing_details carries the ADDRESS here — Afterpay refuses without one
        // for the same reason Affirm does, and stripeBillingDetails() only started
        // sending it on 2026-08-28.
        return state.stripe.confirmAfterpayClearpayPayment(data.clientSecret, {
          payment_method: { billing_details: stripeBillingDetails() },
          return_url: stripeReturnUrl()
        });
      })
      .then(function (result) {
        if (result && result.error) throw stripeError(result.error);
      })
      .catch(handleStripeFailure);
  }

  function payWithAffirm() {
    if (!state.stripe) { setStatus('Affirm is still loading — one moment…', 'info'); return; }
    state.submitting = true;
    togglePayButton(true);
    setStatus('Preparing Affirm…', 'info');
    createStripeIntent()
      .then(function (data) {
        // Leaves the site; nothing after this runs on the happy path.
        // resumeStripeRedirect picks the buyer up on the way back.
        return state.stripe.confirmAffirmPayment(data.clientSecret, {
          payment_method: { billing_details: stripeBillingDetails() },
          return_url: stripeReturnUrl()
        });
      })
      .then(function (result) {
        if (result && result.error) throw stripeError(result.error);
      })
      .catch(handleStripeFailure);
  }

  // ---- shared ------------------------------------------------------------
  function stripeAmountCents() {
    if (state.totals && state.totals.total) {
      var t = Math.round(parseFloat(state.totals.total) * 100);
      if (t > 0) return t;
    }
    if (state.cart && state.cart.total_price > 0) return state.cart.total_price;
    return 0;
  }

  // Keeps the Apple Pay sheet's amount and the Affirm row's eligibility in step
  // with the authoritative total. Never during a payment: the sheet is already
  // showing a figure the buyer has agreed to.
  function syncStripeAmount() {
    // The cart lands after the rails mount, so this is where Apple Pay usually
    // gets its first real chance.
    if (!state.stripePaymentRequest && state.stripe) initStripeApplePay();
    if (state.stripePaymentRequest && !state.submitting) {
      try {
        state.stripePaymentRequest.update({
          total: { label: 'Sofatica', amount: stripeAmountCents() }
        });
      } catch (e) {}
    }
    affirmRowSync(); afterpayRowSync();
  }

  // No address on purpose: Stripe wants an ISO country code and this form stores
  // country NAMES ("United States"), with no map on this side. Guessing would
  // fail every payment; name and email are what the networks match on anyway.
  // What goes on the PaymentMethod at confirm time.
  //
  // The ADDRESS is the load-bearing part, and until 2026-08-28 this function
  // never sent one — only name, email and phone. Cards did not care: Stripe
  // collects whatever the card network wants by itself, so on the rail carrying
  // most of the money the omission was invisible. Affirm does care. Without a
  // billing address the payment is refused before Affirm is even consulted, and
  // the dashboard reports it as "Stripe blocked this payment" with `risk_level:
  // normal`, no Radar rule, and an Affirm transaction id of null — which reads
  // exactly like fraud scoring and is not. One buyer met it nine times in a row
  // on 2026-08-25, $2,617.49 each, then bought elsewhere.
  //
  // The address comes from the SERVER (create-stripe returns `billingDetails`)
  // because building it here is not possible: Shopify stores country and
  // province as full names and Stripe wants ISO codes, and this file has no map
  // for provinces. It is also the only source that works on a pay link, where
  // the page renders no address form at all.
  //
  // The form-read below stays as the fallback for the moment before the intent
  // exists — better a PaymentMethod with a name than one with nothing.
  function stripeBillingDetails() {
    if (state.stripeBillingDetails && state.stripeBillingDetails.address) {
      return state.stripeBillingDetails;
    }
    var nameEl = root.querySelector('[data-st-name]');
    var typed = nameEl && nameEl.value ? nameEl.value.trim() : '';
    var name = typed || (val('first_name') + ' ' + val('last_name')).trim();
    var details = {};
    if (name) details.name = name;
    if (val('email')) details.email = val('email');
    if (val('phone')) details.phone = val('phone');
    return details;
  }

  function createStripeIntent() {
    // Draft-order pay link: there is no cart to price, and getFormData() reads
    // state.cart — which is null in this mode. The backend re-reads the draft's
    // locked total instead, exactly as the PayPal pay-link path does.
    var url = DRAFT ? ENDPOINTS.createStripeForDraft : ENDPOINTS.createStripe;
    var payload = DRAFT ? { draftId: DRAFT.id, token: DRAFT.token } : getFormData();
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(handleJson)
      .then(function (data) {
        if (!data.clientSecret || !data.draftOrderId) throw new Error('no-stripe-intent');
        setDraftOrderId(data.draftOrderId);
        state.stripePaymentIntentId = data.paymentIntentId;
        // The server-built billing address, with country and province already in
        // the ISO codes Stripe wants. See stripeBillingDetails — Affirm is
        // refused without it, and on a pay link this is the only source there is.
        if (data.billingDetails) state.stripeBillingDetails = data.billingDetails;
        // The draft is the price authority; show what it actually says.
        if (data.totals) { state.totals = data.totals; renderTotals(data.totals, null); }
        return data;
      });
  }

  function payWithStripeCard() {
    if (!state.stripe || !state.stripeCardNumber) {
      // The form never came up. Say so, and use the click as the retry — this is
      // the one moment we know the customer is still here and wants to pay.
      setStatus('The card form is still loading — one moment…', 'info');
      mountPaymentRails().then(function () {
        if (state.stripeCardNumber) setStatus('Card details are ready — tap Place order to pay.', 'info');
      });
      return;
    }
    state.submitting = true;
    togglePayButton(true);
    setStatus('Processing payment…', 'info');
    createStripeIntent()
      .then(function (data) {
        return state.stripe.confirmCardPayment(data.clientSecret, {
          payment_method: { card: state.stripeCardNumber, billing_details: stripeBillingDetails() }
        });
      })
      .then(function (result) {
        if (result && result.error) throw stripeError(result.error);
        var pi = result && result.paymentIntent;
        if (!pi) throw new Error('no-payment-intent');
        return captureStripe(pi.id);
      })
      .catch(handleStripeFailure);
  }

  // Stripe's client errors carry text written for the buyer ("Your card was
  // declined."), which beats our generic fallback — but only for the kinds Stripe
  // marks safe to display.
  function stripeError(e) {
    var err = new Error((e && (e.code || e.type)) || 'stripe-error');
    if (e && e.message && (e.type === 'card_error' || e.type === 'validation_error')) {
      err.buyerMessage = e.message;
    }
    return err;
  }

  function handleStripeFailure(err) {
    // captureStripe already reported this one — and may have deliberately locked
    // the pay button because the money moved. Do not undo that.
    if (err && err.ccHandled) return;
    state.submitting = false;
    togglePayButton(false);
    setStatus(
      (err && err.buyerMessage) ||
        friendlyError(err, 'Payment failed. Please check your details and try again.'),
      'error'
    );
  }

  function stripeReturnUrl() {
    return location.origin + location.pathname + devQuery();
  }

  // Never believes the first failure.
  //
  // capture-stripe is idempotent: it returns the existing order when the draft
  // already has one. That matters because Stripe's webhook regularly finishes
  // the draft while this request is still in flight, and the request can then
  // die without an answer — the buyer of order SFT8251449 was told "we took your
  // payment but hit a snag, do not pay again" while that order sat complete and
  // paid in Shopify, with no server log to show for the attempt.
  //
  // So a failed capture asks once more before believing itself. The retry finds
  // the order the webhook built and finishes normally. Only if THAT also fails
  // is something genuinely wrong, and only then is the buyer alarmed.
  function captureStripe(paymentIntentId, isRetry) {
    setStatus(isRetry ? 'Confirming your order…' : 'Finalising your order…', 'info');
    var payload = { paymentIntentId: paymentIntentId };
    // Omitted after a redirect: the backend recovers the draft from the intent's
    // own metadata, which survives a page load that this state does not.
    if (state.draftOrderId) payload.draftOrderId = state.draftOrderId;
    return fetch(ENDPOINTS.captureStripe, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(handleJson)
      .then(function (data) {
        if (!data.success) throw new Error('capture-not-successful');
        state.paymentMethod = data.paymentMethod || null;
        return onOrderComplete(data.order);
      })
      .catch(function (err) {
        var retry = retryCaptureOnce(err, isRetry, function () {
          return captureStripe(paymentIntentId, true);
        });
        if (retry) return retry;
        onCaptureFailed(err, 'We took your payment but hit a snag finalising the order. Please contact us — do not pay again.');
        throw err;
      });
  }

  // Affirm — and a bank forcing a full-page 3-D Secure challenge — send the buyer
  // away and bring them back with the intent on the query string. By then the
  // money may already have moved, so this runs before anything on the page can
  // offer them a second payment.
  // Safari restores a page from the back/forward cache with its JavaScript state
  // intact — so a buyer who leaves for Affirm and presses Back arrives at a page
  // that still believes a payment is in flight, with the pay button disabled and
  // no way to proceed. Chrome mostly reloads instead; Safari does not, and Safari
  // is most of this store's traffic.
  //
  // Only ever unlocks a checkout that has NOT completed: once the thank-you is on
  // screen the lock is correct and must stay.
  function armBfcacheRecovery() {
    window.addEventListener('pageshow', function (ev) {
      if (!ev || !ev.persisted) return;
      if (root.classList.contains('is-complete')) return;
      state.submitting = false;
      togglePayButton(false);
      clearStatusIf('Preparing Affirm…');
      clearStatusIf('Processing payment…');
    });
  }

  function resumeStripeRedirect() {
    var q;
    try { q = new URLSearchParams(location.search); } catch (e) { return false; }
    var pi = q.get('payment_intent');
    if (!pi || !q.get('payment_intent_client_secret')) return false;

    // Scrub the URL so a refresh cannot replay this.
    try { history.replaceState({}, '', location.pathname + devQuery()); } catch (e) {}

    if (q.get('redirect_status') === 'failed') {
      setStatus('That payment was not completed. You can try again or choose another method.', 'info');
      return true;
    }

    // redirect_status can also be `pending`. Whether that money is recoverable is
    // the backend's call, and it answers `held` when the buyer must not pay again.
    state.submitting = true;
    togglePayButton(true);
    captureStripe(pi).catch(function () {});
    return true;
  }

  // -- Authorize.Net (credit card) via Accept.js ----------------------------
  //
  // The live card rail. One row, one method, and no iframe anywhere: Accept.js
  // reads the card out of OUR OWN inputs and hands back a single-use nonce, which
  // the backend charges. That is why .cc-an-input exists in the markup beside the
  // PayPal and Stripe hosts — and it is also why this checkout's PCI scope is now
  // SAQ A-EP rather than SAQ A.
  //
  // What Stripe served from three rows, this serves from one. Apple Pay is not
  // part of Accept.js — it needs Apple Pay JS and a merchant ID registered with
  // Apple — and Authorize.Net does not offer Affirm at all. Both of those rows
  // therefore stay hidden on this gateway, and instalments are the separate
  // Afterpay rail below, which this migration did not touch.
  //
  // THE ORDER IS NOT STRIPE'S, and it is the thing to hold on to:
  //
  //   tokenise the card  ->  create the draft  ->  charge and complete, one call
  //
  // Tokenising first keeps a mistyped card number from leaving an orphan draft
  // behind (Accept.js rejects most bad input before it even reaches the network),
  // and a nonce lives 15 minutes, which is ample cover for a draft that takes
  // seconds to price.
  function isAuthNetCard() { return CONFIG.cardGateway === 'authorize'; }
  function isPayPalCard() { return CONFIG.cardGateway === 'paypal'; }

  function loadAcceptJs(url) {
    return new Promise(function (resolve, reject) {
      if (window.Accept) return resolve(window.Accept);
      var el = document.createElement('script');
      el.src = url;
      el.onload = function () {
        // Accept.js does not always have window.Accept defined by the time onload
        // fires — it finishes wiring itself up a tick or two later, and calling
        // dispatchData before then throws a bare "Accept is not defined" that
        // reads like the script failed to load at all. Poll briefly instead.
        var tries = 0;
        (function wait() {
          if (window.Accept) return resolve(window.Accept);
          if (++tries > 40) return reject(new Error('Accept.js loaded but did not initialise'));
          setTimeout(wait, 50);
        })();
      };
      el.onerror = function () { reject(new Error('Failed to load Accept.js')); };
      document.head.appendChild(el);
    });
  }

  function anFields() {
    return {
      number: root.querySelector('[data-an-number]'),
      expiry: root.querySelector('[data-an-expiry]'),
      cvc:    root.querySelector('[data-an-cvc]'),
      name:   root.querySelector('[data-an-name]')
    };
  }

  // ---- typing ------------------------------------------------------------
  // The Stripe elements did all of this inside their iframe. These fields are
  // ours, so the grouping, the slash and the digit limits are ours to draw too —
  // a card number that stays one 16-digit run while it is typed reads as broken
  // long before anyone checks whether it works.

  function groupCardNumber(raw) {
    var d = raw.replace(/\D/g, '').slice(0, 19);
    // Amex is 4-6-5, every other network 4-4-4-4(-3). Getting this wrong charges
    // the card perfectly well and still makes a familiar number look wrong.
    var sizes = /^3[47]/.test(d) ? [4, 6, 5] : [4, 4, 4, 4, 3];
    var out = [], at = 0;
    for (var i = 0; i < sizes.length && at < d.length; i++) {
      out.push(d.substr(at, sizes[i]));
      at += sizes[i];
    }
    return out.join(' ');
  }

  function groupExpiry(raw) {
    var d = raw.replace(/\D/g, '').slice(0, 4);
    if (!d) return '';
    // A lone digit above 1 can only be a month: "5" is May, not the start of a
    // year, so it becomes "05" and the buyer never has to type the zero.
    if (d.length === 1 && d > '1') d = '0' + d;
    return d.length < 3 ? d : d.slice(0, 2) + ' / ' + d.slice(2);
  }

  function expiryParts(raw) {
    var d = raw.replace(/\D/g, '');
    if (d.length < 3) return null;
    // Accept.js takes a two-digit year happily, and two digits is all the buyer
    // typed — expanding it here would only invent a century.
    return { month: d.slice(0, 2), year: d.slice(2, 4) };
  }

  function bindAuthNetField(el, format) {
    if (!el) return;
    el.addEventListener('focus', function () { el.classList.remove('is-invalid'); });
    if (!format) return;
    el.addEventListener('input', function () {
      // Reformatted only while the caret is at the end. Rewriting the value
      // mid-string moves the caret to the end with it, so a buyer correcting one
      // digit in the middle of their card number gets thrown to the far end of
      // the field on every keystroke.
      if (el.selectionStart !== el.value.length) return;
      var next = format(el.value);
      if (next !== el.value) el.value = next;
    });
  }

  function mountAuthNetCard() {
    // Loudly, not silently: a resolved promise here would skip the setPayReady
    // handover below and leave the button reading "Loading payment…" for ever.
    var f = anFields();
    if (!f.number) {
      return Promise.reject(new Error('Authorize.Net card inputs are missing from the page'));
    }
    // The login id and client key come from the backend, not a theme setting, so
    // they are always from the same account AND the same environment as the
    // transaction key that will charge the card. A sandbox nonce presented to the
    // live API fails as "E00114 Invalid OTS Token" at the buyer's last click,
    // which is the worst possible place to discover a mismatched pair.
    return fetch(ENDPOINTS.authnetConfig, { headers: { Accept: 'application/json' } })
      .then(handleJson)
      .then(function (cfg) {
        if (!cfg || !cfg.publicClientKey || !cfg.apiLoginId) throw new Error('authnet-not-configured');
        state.authnetLoginId = cfg.apiLoginId;
        state.authnetClientKey = cfg.publicClientKey;
        // The script URL comes back with the keys for the same reason the keys do:
        // jstest.authorize.net and js.authorize.net mint nonces the other side's
        // API will not accept, so the pair is decided in one place.
        return loadAcceptJs(cfg.acceptJsUrl);
      })
      .then(function () {
        bindAuthNetField(f.number, groupCardNumber);
        bindAuthNetField(f.expiry, groupExpiry);
        bindAuthNetField(f.cvc, function (v) { return v.replace(/\D/g, '').slice(0, 4); });
        bindAuthNetField(f.name, null);
        showCardSlots('authnet');
        state.authnetReady = true;
        show($('[data-payment-section]'));
        setPayReady(true);
      });
  }

  // ---- tokenising --------------------------------------------------------

  // Accept.js reports its own validation with E_WC_* codes and a sentence written
  // for a buyer ("Credit card number is invalid."), which beats our generic
  // fallback. The code doubles as the field to mark.
  var ACCEPT_FIELD = {
    E_WC_05: 'number', E_WC_06: 'expiry', E_WC_07: 'expiry', E_WC_08: 'expiry',
    E_WC_15: 'cvc', E_WC_16: 'cvc', E_WC_17: 'name', E_WC_19: 'number'
  };

  function acceptError(messages) {
    var m = messages && messages.message && messages.message[0];
    var err = new Error((m && m.code) || 'accept-error');
    if (m && m.text) err.buyerMessage = m.text;
    var field = m && ACCEPT_FIELD[m.code];
    if (field) {
      var el = anFields()[field];
      if (el) { el.classList.add('is-invalid'); try { el.focus(); } catch (e) {} }
    }
    return err;
  }

  function tokenizeAuthNetCard() {
    var f = anFields();
    var number = (f.number && f.number.value || '').replace(/\s/g, '');
    var exp = expiryParts(f.expiry ? f.expiry.value : '');
    var cvc = (f.cvc && f.cvc.value || '').trim();
    // The delivery form already has a name; the card field is only there for the
    // buyer whose card carries a different one.
    var name = (f.name && f.name.value || '').trim() || (val('first_name') + ' ' + val('last_name')).trim();

    // Checked here so the buyer is told WHICH box is empty. Accept.js would
    // answer the same question, but only after a round trip, and only for the
    // first field it happens to look at.
    var missing = null;
    if (!number) missing = 'number';
    else if (!exp) missing = 'expiry';
    else if (!cvc) missing = 'cvc';
    if (missing) {
      var el = f[missing];
      if (el) { el.classList.add('is-invalid'); try { el.focus(); } catch (e) {} }
      var err = new Error('card-incomplete');
      err.buyerMessage = 'Please complete your card details.';
      return Promise.reject(err);
    }

    return new Promise(function (resolve, reject) {
      // No zip is sent. AVS is scored on what the SERVER submits, and the server
      // uses the draft's own billing address — which is the one the buyer
      // actually typed, rather than whichever of this page's two billing blocks
      // happens to be on screen.
      window.Accept.dispatchData(
        {
          authData: { clientKey: state.authnetClientKey, apiLoginID: state.authnetLoginId },
          cardData: { cardNumber: number, month: exp.month, year: exp.year, cardCode: cvc, fullName: name }
        },
        function (res) {
          var msgs = res && res.messages;
          if (!msgs || msgs.resultCode !== 'Ok') return reject(acceptError(msgs));
          var od = res.opaqueData;
          if (!od || !od.dataValue) return reject(new Error('accept-no-token'));
          resolve(od);
        }
      );
    });
  }

  // ---- paying ------------------------------------------------------------

  function createAuthNetDraft() {
    // Draft-order pay link: there is no cart to price, and getFormData() reads
    // state.cart — which is null in this mode. The backend re-reads the draft's
    // locked total instead, exactly as the PayPal and Stripe pay-link paths do.
    var url = DRAFT ? ENDPOINTS.createAuthnetForDraft : ENDPOINTS.createAuthnet;
    var payload = DRAFT ? { draftId: DRAFT.id, token: DRAFT.token } : getFormData();
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(handleJson)
      .then(function (data) {
        if (!data.draftOrderId) throw new Error('no-authnet-draft');
        setDraftOrderId(data.draftOrderId);
        // The draft is the price authority; show what it actually says.
        if (data.totals) { state.totals = data.totals; renderTotals(data.totals, null); }
        return data;
      });
  }

  function payWithAuthNetCard() {
    if (!state.authnetReady || !window.Accept) {
      // The form never came up. Say so, and use the click as the retry — this is
      // the one moment we know the customer is still here and wants to pay.
      setStatus('The card form is still loading — one moment…', 'info');
      mountPaymentRails().then(function () {
        if (state.authnetReady) setStatus('Card details are ready — tap Place order to pay.', 'info');
      });
      return;
    }
    state.submitting = true;
    togglePayButton(true);
    setStatus('Checking your card…', 'info');
    tokenizeAuthNetCard()
      .then(function (opaque) {
        setStatus('Processing payment…', 'info');
        return createAuthNetDraft().then(function () { return captureAuthNet(opaque); });
      })
      .catch(function (err) {
        reportAuthNetFailure(err);
        handleAuthNetFailure(err);
      });
  }

  // Every way a press of Place order can end without an order on this rail, as a
  // rail event. On 2026-09-21, 32 buyers pressed Place order and never paid, and
  // none of them could be attributed, because this rail — the store's main card
  // rail — was the one rail that reported nothing.
  //
  // Only the Accept.js codes tied to a field count as the buyer's typing. Any
  // other code (E_WC_21 is a rejected login id / client key) is ours to fix and
  // must not hide inside a pile of typos.
  function reportAuthNetFailure(err) {
    var body = (err && err.body) || null;
    var code = (err && err.message) || '';
    var event = 'error';
    if (err && err.railEvent) event = err.railEvent;
    else if (body && body.held) event = 'held';
    else if (code === 'card-incomplete' || ACCEPT_FIELD[code]) event = 'invalid-card';
    reportRailEvent('card', event, state.draftOrderId, code);
  }

  function handleAuthNetFailure(err) {
    // captureAuthNet already reported this one — and may have deliberately locked
    // the pay button because the money moved. Do not undo that.
    if (err && err.ccHandled) return;
    state.submitting = false;
    togglePayButton(false);
    setStatus(
      (err && err.buyerMessage) ||
        friendlyError(err, 'Payment failed. Please check your card details and try again.'),
      'error'
    );
  }

  // Never believes the first failure — and unlike every other capture in this
  // file, that is a claim that had to be earned.
  //
  // On the other rails a capture only READS a payment someone else already took,
  // so repeating it costs nothing. capture-authnet TAKES the payment, and a blind
  // retry here would be a second charge on a four-figure sofa.
  //
  // What makes the retry safe is on the server: before charging, capture-authnet
  // asks Authorize.Net whether it is already holding a charge for this draft, and
  // recovers it instead of taking another. The nonce below is single-use, so the
  // second attempt cannot charge again even if that guard were to miss — it would
  // be refused as a spent token rather than taken twice.
  //
  // A `held` payment is never retried: that is a real answer, not a lost one, and
  // onCaptureFailed has already locked the button on purpose.
  function captureAuthNet(opaque, isRetry) {
    setStatus(isRetry ? 'Confirming your order…' : 'Finalising your order…', 'info');
    return fetch(ENDPOINTS.captureAuthnet, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        draftOrderId: state.draftOrderId,
        opaqueDataDescriptor: opaque.dataDescriptor,
        opaqueDataValue: opaque.dataValue
      })
    })
      .then(handleJson)
      .then(function (data) {
        if (!data.success) throw new Error('capture-not-successful');
        state.paymentMethod = data.paymentMethod || null;
        return onOrderComplete(data.order);
      })
      .catch(function (err) {
        var body = (err && err.body) || null;

        // A REAL answer, not a lost one — and this branch exists only on this
        // rail, because only on this rail does the decline come back from our own
        // server instead of from the gateway's iframe.
        //
        // Without it a declined card would be retried for 1.8 seconds under
        // "Confirming your order…" and then reported with onCaptureFailed's
        // sentence: "we took your payment but hit a snag — do not pay again."
        // That is false, it locks the pay button, and it turns a buyer who only
        // needed to reach for a second card into a lost four-figure sale.
        if (body && body.retryable) {
          var answered = new Error(body.status || 'declined');
          answered.buyerMessage = body.message || 'Your card was declined. Please try a different card.';
          answered.railEvent = 'declined';
          throw answered;
        }

        // Held, or a charge whose fate we cannot prove. Also a real answer: the
        // money may have moved, so it goes straight to the handler that says so
        // and leaves the button locked. Retrying could not change it.
        if (body && body.held) {
          onCaptureFailed(err, 'We could not confirm your payment. Please contact us — do not pay again.');
          throw err;
        }

        var retry = retryCaptureOnce(err, isRetry, function () {
          return captureAuthNet(opaque, true);
        });
        if (retry) return retry;
        onCaptureFailed(err, 'We took your payment but hit a snag finalising the order. Please contact us — do not pay again.');
        throw err;
      });
  }

  // -- Afterpay (Cash App Afterpay) via afterpay.js popup --------------------
  // Flow: on "Pay now" (Afterpay selected) open a blank popup synchronously,
  // create the Afterpay checkout on our backend, transfer the popup to it, then
  // capture on SUCCESS. The popup MUST be opened inside the click gesture.
  function loadAfterpaySdk() {
    return new Promise(function (resolve, reject) {
      if (window.AfterPay) return resolve(window.AfterPay);
      var s = document.createElement('script');
      s.src = 'https://portal.afterpay.com/afterpay.js';
      s.onload = function () { resolve(window.AfterPay); };
      s.onerror = function () { reject(new Error('Failed to load Afterpay')); };
      document.head.appendChild(s);
    });
  }

  // Load afterpay.js in the background when the customer shows intent (selects
  // Afterpay). We deliberately do NOT initialize() here — initialize + open both
  // run inside the Pay-now click, so afterpay.js never renders its overlay on
  // page load (initialize() inspects the URL and can show the loader overlay).
  function preloadAfterpay() {
    if (window.AfterPay || state.afterpayLoading) return;
    state.afterpayLoading = true;
    loadAfterpaySdk().catch(function () { state.afterpayLoading = false; });
  }

  // Afterpay handed control back. Either it made an order, or it did not.
  //
  // The `else` here used to be three lines that cleared the status and said
  // nothing — no message, no alternative, no record. On 2026-09-04 a buyer met
  // that silence five times in fifty minutes: press Pay now, Afterpay window,
  // back to a checkout that looked exactly as it had before, no explanation of
  // what went wrong or what to try instead. She left three open drafts for the
  // same $1,730.93 sofa and bought nothing. Afterpay's merchant portal had no
  // record of her at all — not an approval, not a decline — so the bounce
  // happened inside their flow, before an order existed. The likeliest reason by
  // far is their per-consumer spending limit, which is theirs to decide and is
  // never disclosed to us.
  //
  // So this cannot say WHY, and it must not pretend to. What it can honestly do
  // is confirm nothing was charged, name the most likely cause as a possibility,
  // and point at the two rails sitting directly above Afterpay in the list.
  function afterpayComplete(event) {
    var status = (event && event.data && event.data.status) || 'UNKNOWN';
    if (status === 'SUCCESS') {
      captureAfterpay(event.data.orderToken || state.afterpayToken);
      return;
    }
    state.submitting = false;
    togglePayButton(false);
    // The draft is the tie back to what the merchant sees in the admin: the open
    // draft this attempt left behind now has a log line explaining itself.
    reportRailEvent('afterpay', 'not-completed', state.afterpayDraftId, status);
    setStatus(
      'Afterpay did not complete this order, and nothing was charged. This often means the ' +
        'total is above your Afterpay spending limit — that is Afterpay’s decision and they ' +
        'do not tell us the reason. You can try again, or pay by credit card or PayPal above.',
      'error'
    );
  }

  function startAfterpay() {
    if (!validateForm()) { setStatus(validationStatus(), 'error'); return; }
    if (!window.AfterPay) {
      preloadAfterpay();
      setStatus('Afterpay is still loading — please tap Pay now again in a moment.', 'error');
      return;
    }
    // Initialize + wire the completion handler ONCE, inside the click gesture.
    if (!state.afterpayInitDone) {
      window.AfterPay.initialize({ countryCode: 'US' });
      window.AfterPay.onComplete = afterpayComplete;
      state.afterpayInitDone = true;
    }
    state.submitting = true;
    togglePayButton(true);
    setStatus('Opening Afterpay…', 'info');
    // Cleared before the attempt, not after it. A buyer who closes the popup
    // before create-afterpay resolves must report no draft at all rather than
    // the one a previous attempt left behind — a log line pointing at the wrong
    // draft is worse than one pointing at none.
    state.afterpayDraftId = null;
    // 1. Open the popup NOW (inside the user gesture) so it isn't blocked.
    window.AfterPay.open();
    // 2. Create the Afterpay checkout for the authoritative total, then transfer
    //    the already-open popup to it.
    fetch(ENDPOINTS.createAfterpay, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(getFormData())
    })
      .then(handleJson)
      .then(function (data) {
        if (!data.afterpayToken) throw new Error('no-afterpay-token');
        state.afterpayToken = data.afterpayToken;
        setDraftOrderId(data.draftOrderId);
        state.afterpayDraftId = data.draftOrderId || null;
        window.AfterPay.transfer({ token: data.afterpayToken });
      })
      .catch(function (err) {
        state.submitting = false;
        togglePayButton(false);
        try { window.AfterPay.close(); } catch (e) {}
        setStatus(friendlyError(err, 'We could not start Afterpay. Please try again or choose another method.'), 'error');
      });
  }

  function captureAfterpay(afterpayToken, isRetry) {
    setStatus('Finalising your order…', 'info');
    return fetch(ENDPOINTS.captureAfterpay, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ afterpayToken: afterpayToken, draftOrderId: state.draftOrderId })
    })
      .then(handleJson)
      .then(function (data) {
        if (!data.success) throw new Error('capture-not-successful');
        state.paymentMethod = data.paymentMethod || { type: 'afterpay' };
        return onOrderComplete(data.order);
      })
      .catch(function (err) {
        var retry = retryCaptureOnce(err, isRetry, function () {
          setStatus('Confirming your order…', 'info');
          return captureAfterpay(afterpayToken, true);
        });
        if (retry) return retry;
        onCaptureFailed(err, 'We could not finalise your Afterpay order. Please contact us — do not pay again.');
        throw err;
      });
  }

  // -- Affirm (BNPL) via affirm.js modal -------------------------------------
  // Flow: on "Pay now" (Affirm selected) create the Affirm checkout on our backend,
  // then affirm.checkout(obj) + affirm.checkout.open({onSuccess}) opens the modal;
  // on approval Affirm returns a checkout_token → capture-affirm (authorize→capture→complete).
  function loadAffirm() {
    if (window.affirm && window.affirm.checkout) return;
    if (state.affirmLoading || !CONFIG.affirmKey) return;
    state.affirmLoading = true;
    var cdn = CONFIG.affirmEnv === 'sandbox' ? 'https://cdn.sandbox.affirm.com' : 'https://cdn.affirm.com';
    var cfg = (window._affirm_config = { public_api_key: CONFIG.affirmKey, script: cdn + '/js/v2/affirm.js' });
    // Official Affirm.js loader snippet (queues calls until affirm.js loads).
    (function (l, g, m, e, a, f, b) { var d, c = l[m] || {}, h = document.createElement(f), n = document.getElementsByTagName(f)[0], k = function (a, b, c) { return function () { a[b]._.push([c, arguments]); }; }; c[e] = k(c, e, 'set'); d = c[e]; c[a] = {}; c[a]._ = []; d._ = []; c[a][b] = k(c[a], b, a); a = 0; for (b = 'set add save post open empty reset on off trigger ready setProduct'.split(' '); a < b.length; a++) d[b[a]] = k(c, e, b[a]); a = 0; for (b = ['get', 'token', 'url', 'items']; a < b.length; a++) d[b[a]] = function () {}; h.async = !0; h.src = g[f]; n.parentNode.insertBefore(h, n); delete g[f]; d.ui = { error: { on: function () {}, off: function () {} } }; l[m] = c; })(window, cfg, 'affirm', 'checkout', 'ui', 'script');
  }

  function startAffirm() {
    if (!validateForm()) { setStatus(validationStatus(), 'error'); return; }
    if (!CONFIG.affirmKey) { setStatus('Affirm is unavailable right now. Please choose another method.', 'error'); return; }
    loadAffirm();
    state.submitting = true;
    togglePayButton(true);
    setStatus('Preparing Affirm…', 'info');
    fetch(ENDPOINTS.createAffirm, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(getFormData())
    })
      .then(handleJson)
      .then(function (data) {
        if (!data.checkout || !data.draftOrderId) throw new Error('no-affirm-checkout');
        setDraftOrderId(data.draftOrderId);
        window.affirm.checkout(data.checkout);
        window.affirm.checkout.open({
          onFail: function () {
            state.submitting = false;
            togglePayButton(false);
            setStatus('Affirm was cancelled. You can try again or choose another method.', 'info');
          },
          onSuccess: function (a) { captureAffirm(a && a.checkout_token); }
        });
      })
      .catch(function (err) {
        state.submitting = false;
        togglePayButton(false);
        setStatus(friendlyError(err, 'We could not start Affirm. Please try again or choose another method.'), 'error');
      });
  }

  function captureAffirm(checkoutToken) {
    if (!checkoutToken) {
      state.submitting = false; togglePayButton(false);
      setStatus('Affirm did not return a confirmation. Please try again.', 'error');
      return;
    }
    setStatus('Finalising your order…', 'info');
    return fetch(ENDPOINTS.captureAffirm, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ checkoutToken: checkoutToken, draftOrderId: state.draftOrderId })
    })
      .then(handleJson)
      .then(function (data) {
        if (!data.success) throw new Error('capture-not-successful');
        state.paymentMethod = data.paymentMethod || { type: 'affirm' };
        return onOrderComplete(data.order);
      })
      .catch(function (err) {
        onCaptureFailed(err, 'We could not finalise your Affirm order. Please contact us — do not pay again.');
        throw err;
      });
  }

  // Format the capture's payment method: "Visa •••• 1234", "PayPal", "Apple Pay".
  // Falls back to the selected method only if the capture didn't report one.
  function formatPaymentMethod(pm) {
    if (pm && pm.type === 'card') {
      var brand = pm.brand ? pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1).toLowerCase() : 'Card';
      return pm.last4 ? (brand + ' •••• ' + pm.last4) : brand;
    }
    if (pm && pm.type === 'paypal') return 'PayPal';
    if (pm && pm.type === 'apple_pay') return 'Apple Pay';
    if (pm && pm.type === 'google_pay') return 'Google Pay';
    if (pm && pm.type === 'afterpay') return 'Afterpay';
    if (pm && pm.type === 'affirm') return 'Affirm';
    var el = root.querySelector('[name="paymethod"]:checked');
    var labels = { card: 'Credit card', applepay: 'Apple Pay', paypal: 'PayPal', afterpay: 'Afterpay', affirm: 'Affirm' };
    return labels[el ? el.value : 'card'] || 'Credit card';
  }

  // Shopify-style order confirmation: hide the checkout grid, show stacked cards.
  function showThankYou(order) {
    // The order exists; nothing is in flight any more.
    hidePayOverlay();

    // Safety net: re-assert the authoritative order totals on the summary so the
    // confirmation ALWAYS shows the real amounts (never a stale/zeroed total).
    if (state.totals) renderTotals(state.totals, null);

    var firstName = val('first_name');
    var lastName = val('last_name');
    var email = val('email');

    setText('[data-ty-ordername]', (order && order.name) || '');
    setText('[data-ty-name]', firstName ? ', ' + firstName : '');
    setText('[data-ty-email]', email ? ' to ' + email : '');

    // Order date, e.g. " Jul 28" (browser-local; the order is created just now).
    try {
      var d = new Date();
      var mo = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      setText('[data-ty-date]', ' ' + mo[d.getMonth()] + ' ' + d.getDate());
    } catch (e) {}

    // Customer information card. (Order summary stays in the right column — the
    // 2-column checkout layout is preserved, so no cloning needed here.)
    setText('[data-ty-contact]', email || '');
    var shipTo = [
      (firstName + ' ' + lastName).trim(),
      val('address1'),
      val('address2'),
      [val('city'), val('province'), val('zip')].filter(Boolean).join(' '),
      val('country'),
      val('phone')
    ].filter(Boolean).join(', ');
    setText('[data-ty-shipto]', shipTo);

    // Billing address: "Same as shipping" when the toggle is on, else the billing form.
    var billingToggle = root.querySelector('[data-billing-toggle]');
    var billing;
    if (!billingToggle || billingToggle.checked) {
      billing = 'Same as shipping address';
    } else {
      billing = [
        (val('billing_first_name') + ' ' + val('billing_last_name')).trim(),
        val('billing_address1'),
        val('billing_address2'),
        [val('billing_city'), val('billing_province'), val('billing_zip')].filter(Boolean).join(' '),
        val('billing_country')
      ].filter(Boolean).join(', ');
    }
    setText('[data-ty-billing]', billing);

    setText('[data-ty-method]', state.shipTitle || '—');

    // Shipping-location map banner (Google Static Maps, reusing the Places key).
    // Shown only if the image actually loads (key must have Static Maps enabled).
    var mapAddr = [val('address1'), val('city'), val('province'), val('zip'), val('country')]
      .filter(Boolean).join(', ');
    var mapWrap = root.querySelector('[data-ty-map]');
    var mapImg = root.querySelector('[data-ty-map-img]');
    if (CONFIG.gmapsKey && mapAddr && mapWrap && mapImg) {
      var q = encodeURIComponent(mapAddr);
      mapImg.onload = function () { mapWrap.hidden = false; };
      mapImg.onerror = function () { mapWrap.hidden = true; };
      mapImg.src = 'https://maps.googleapis.com/maps/api/staticmap?zoom=15&size=640x180&scale=2&maptype=roadmap'
        + '&center=' + q
        + '&markers=' + encodeURIComponent('color:red|' + mapAddr)
        + '&key=' + encodeURIComponent(CONFIG.gmapsKey);
    }
    // Payment = what they paid WITH, from the PayPal capture (authoritative).
    setText('[data-ty-payment]', formatPaymentMethod(state.paymentMethod));

    var viewBtn = root.querySelector('[data-ty-vieworder]');
    if (viewBtn) {
      if (order && order.statusUrl) viewBtn.setAttribute('href', order.statusUrl);
      else viewBtn.style.display = 'none';
    }

    root.classList.add('is-complete');
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) { window.scrollTo(0, 0); }
  }

  function onOrderComplete(order) {
    // This draft is an order now. Leaving its id behind would hand the next
    // checkout in this browser a `supersedes` pointing at a PAID draft — refused
    // by the server, but there is no reason to ask.
    forgetDraft();
    // Publish BEFORE clearing the cart — analyticsLineItems() reads state.cart.
    publishCheckoutCompleted(order);
    // Clear the Shopify cart, then show the thank-you (regardless of clear result).
    return fetch('/cart/clear.js', { method: 'POST' }).then(
      function () { showThankYou(order); },
      function () { showThankYou(order); }
    );
  }

  // -- misc helpers ----------------------------------------------------------
  function handleJson(r) {
    return r.json().then(function (body) {
      if (!r.ok) {
        var e = new Error(body && body.error ? body.error : 'request-failed');
        e.status = r.status; e.body = body;
        // Server errors are NOT shown to buyers by default, and that is right: a
        // raw one is as likely to be a stack trace as an explanation, which is
        // why friendlyError throws the text away outside test mode.
        //
        // But a few are written FOR the buyer and are the only useful thing we
        // could say. The server marks those explicitly rather than us guessing
        // from a status code. `unshippable` is the first: an address Shopify will
        // not deliver to needs the buyer to change the address, and telling them
        // "Payment failed. Please check your details and try again." would send
        // them round the card form forever over something no card can fix.
        if (body && body.unshippable && body.error) e.buyerMessage = body.error;
        // Same reasoning: an address with no house number needs the buyer to add
        // one, and no amount of retrying the card will produce it.
        if (body && body.missingStreetNumber && body.error) e.buyerMessage = body.error;
        throw e;
      }
      return body;
    });
  }
  function friendlyError(err, fallback) {
    if (CONFIG.testMode && err) return fallback + ' [' + (err.message || err) + ']';
    return fallback;
  }
  // Tell the backend about something that happened to a rail inside this browser.
  //
  // A rail that hands control to a popup or a redirect can come back with nothing,
  // and from the server that outcome is indistinguishable from a buyer who never
  // pressed the button. On 2026-09-04 one buyer bounced out of Afterpay five times
  // in fifty minutes; every endpoint answered 200, no capture was ever called, and
  // Afterpay's own portal had no record of her, so nothing anywhere could say what
  // had happened. This is the wire that would have said it.
  //
  // sendBeacon rather than fetch: the buyer is often mid-navigation when a rail
  // gives up, and a beacon survives the unload that would cancel a fetch. Failure
  // is ignored at every level — a diagnostic must never be something the buyer can
  // feel, and the backend answers 204 to everything for the same reason.
  // text/plain, not application/json, and that is load-bearing. Any other content
  // type puts this cross-origin request outside the CORS safelist, which means a
  // preflight — and sendBeacon cannot preflight, so the beacon would be dropped by
  // the browser with no error anywhere. The backend reads the raw body and parses
  // it itself, so the header costs nothing.
  var RAIL_EVENT_TYPE = 'text/plain;charset=UTF-8';
  // The backend line above lands in Vercel logs, which keep less than two days —
  // enough to explain one buyer the same afternoon, never enough to say which rail
  // loses the most. Measured 2026-09-21: 32 buyers pressed Place order and never
  // paid (~$73k of carts), and not one of those bounces could be attributed. So the
  // same fact also goes to PostHog, where it stays queryable next to the funnel.
  // Its own try: a missing or broken posthog must not cost the backend its line.
  var RAIL_DETAIL_MAX = 120;
  function captureRailEvent(rail, event, draftOrderId, detail) {
    try {
      if (!window.posthog || typeof window.posthog.capture !== 'function') return;
      // card_gateway: `rail: 'card'` is shared by PayPal's card fields and
      // Authorize.Net's, and the backend accepts no other card rail name. Without
      // this the two gateways' failures are one indistinguishable pile.
      window.posthog.capture('checkout_rail_event', {
        rail: rail,
        card_gateway: CONFIG.cardGateway,
        rail_event: event,
        draft_order_id: draftOrderId || null,
        detail: String(detail || '').slice(0, RAIL_DETAIL_MAX)
      }, { transport: 'sendBeacon' });
    } catch (e) {}
  }
  function reportRailEvent(rail, event, draftOrderId, detail) {
    captureRailEvent(rail, event, draftOrderId, detail);
    try {
      var payload = JSON.stringify({
        rail: rail,
        event: event,
        draftOrderId: draftOrderId || null,
        detail: detail || ''
      });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(ENDPOINTS.railEvent, new Blob([payload], { type: RAIL_EVENT_TYPE }));
        return;
      }
      fetch(ENDPOINTS.railEvent, {
        method: 'POST',
        headers: { 'Content-Type': RAIL_EVENT_TYPE },
        body: payload,
        keepalive: true
      }).catch(function () {});
    } catch (e) {}
  }
  // Shared handler for a capture that did not produce an order.
  //
  // A HELD payment is not something the buyer can fix by trying again: the money has
  // already left their account and the gateway is reviewing it. Re-enabling the pay
  // button in that state is what turned one purchase into five captures of $2,759.08
  // on 2026-08-11 — the backend answered "Payment not completed" for a PENDING
  // capture, the buyer read it as a failure, and paid again. Four more times.
  //
  // So when the backend sets `held`, show what it says and leave the button disabled.
  // Everything else keeps the old behaviour on purpose: a genuine decline SHOULD be
  // retryable, and locking the button there would turn a recoverable checkout into a
  // lost sale.
  function onCaptureFailed(err, fallback) {
    state.submitting = false;
    // Mark the error as reported so an outer handler cannot overwrite this message.
    // captureOrder rethrows, and for cards that rejection propagates all the way out
    // to cardField.submit().catch — which used to replace "we took your money, don't
    // pay again" with "check your card details and try again" AND re-enable the
    // button. On the most common payment path, that undid everything this function
    // does.
    if (err && typeof err === 'object') err.ccHandled = true;
    var body = (err && err.body) || null;
    if (body && body.held) {
      setStatus(
        body.message ||
          'Your payment is being reviewed by the payment provider. Do not pay again — we will email you as soon as it clears.',
        'error'
      );
      // Deliberately NOT togglePayButton(false) — the button stays disabled. Clearing
      // is-loading separately so it reads as blocked rather than still working.
      var btn = $('[data-pay-button]');
      if (btn) {
        btn.disabled = true;
        btn.classList.remove('is-loading');
      }
      return;
    }
    // The backend PROVED the money did not move — an abandoned 3-D Secure window
    // (`requires_action`) or a decline (`requires_payment_method`). It writes a
    // message saying what the buyer should do; show that, and leave the button
    // usable so they can.
    //
    // Without this the buyer fell through to the caller's fallback, which for a
    // capture reads "We took your payment … do not pay again". That sentence is
    // RIGHT when a capture dies midway — on this rail the money really has moved
    // by the time capture runs — and wrong in exactly the two cases the backend
    // can rule out. So a buyer who merely closed the 3DS window was told their
    // card had been charged and that they must not try again: a lost sale
    // invented out of caution, and the same mistake commit c14a324 removed from
    // the Authorize.Net rail. The rule holds on both sides of the wire now —
    // never claim money moved without proof, and never claim it did not.
    //
    // friendlyError is bypassed deliberately: it discards the server's text
    // outside test mode, which is correct for a raw error and wrong for a
    // sentence written for this buyer.
    if (body && body.retryable && body.message) {
      togglePayButton(false);
      setStatus(body.message, 'error');
      return;
    }
    togglePayButton(false);
    setStatus(friendlyError(err, fallback), 'error');
  }
  // Update ALL matching nodes so the top summary and the mobile mirror stay in sync.
  function setText(sel, text) {
    root.querySelectorAll(sel).forEach(function (el) { el.textContent = text; });
  }
  function showSel(sel) { root.querySelectorAll(sel).forEach(function (el) { el.hidden = false; }); }
  function hideSel(sel) { root.querySelectorAll(sel).forEach(function (el) { el.hidden = true; }); }
  // "Place order" is live from the moment the HTML paints, but until the card
  // fields exist a click on it did nothing at all — pay() returned silently. So
  // it says what it is doing and stays locked until the rails have settled (either
  // mounted, or failed and handled).
  function setPayReady(ready) {
    state.payReady = !!ready;
    var btn = $('[data-pay-button]');
    if (!btn) return;
    if (!btn.getAttribute('data-label')) btn.setAttribute('data-label', btn.textContent.trim());
    btn.disabled = !ready;
    btn.textContent = ready ? btn.getAttribute('data-label') : 'Loading payment…';
  }
  // The overlay is dismissed from two places, because the two endings are not
  // symmetrical: a failure unwinds the button, a success replaces the page and
  // never touches it again. Hooking only togglePayButton() left the wait screen
  // covering a finished thank-you page — order SFT8251450 was confirmed and
  // invisible behind it.
  function hidePayOverlay() {
    var overlay = root.querySelector('[data-pay-overlay]');
    if (overlay) overlay.hidden = true;
    document.documentElement.style.overflow = '';
  }

  function togglePayButton(loading) {
    // A spinner inside a button is easy to miss on a long page — the buyer can
    // be scrolled far from it, and nothing else says the order is being placed.
    // Between the card confirm and the order being built there are several
    // seconds and two round trips; an unlocked-looking page invites a second
    // click on the very rail where a double payment costs the most.
    if (loading) {
      var overlay = root.querySelector('[data-pay-overlay]');
      if (overlay) overlay.hidden = false;
      document.documentElement.style.overflow = 'hidden';
    } else {
      hidePayOverlay();
    }

    var btn = $('[data-pay-button]');
    if (!btn) return;
    // Never hand the button back while the rails are still coming up.
    btn.disabled = loading || !state.payReady;
    btn.classList.toggle('is-loading', loading);
  }
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // -- Google Places address autocomplete -----------------------------------
  function setFieldValue(name, value) {
    var el = root.querySelector('[name="' + name + '"]');
    if (!el || value == null) return;
    el.value = value;
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function fillFromPlace(components, f) {
    function longName(type) {
      for (var i = 0; i < components.length; i++) {
        if (components[i].types.indexOf(type) >= 0) return components[i].long_name;
      }
      return '';
    }
    var streetNo = longName('street_number');
    var route = longName('route');
    // Places returns a route with NO street_number when the buyer picks a
    // STREET-level suggestion instead of a full address. This used to write it
    // back regardless, which replaced whatever they had typed with a strictly
    // worse version of it — "1425 Dovedale Street" became "Dovedale Street" —
    // and nothing downstream objected, because the box was not empty. So a
    // numberless route only ever fills a field that is already EMPTY; it never
    // overwrites the buyer, and validateForm asks for the number at submit.
    if (streetNo) setFieldValue(f.address1, streetNo + ' ' + route);
    else if (route && !val(f.address1)) setFieldValue(f.address1, route);
    setFieldValue(f.city, longName('locality') || longName('postal_town') || longName('sublocality') || longName('sublocality_level_1'));
    setFieldValue(f.province, longName('administrative_area_level_1'));
    setFieldValue(f.zip, longName('postal_code'));
  }

  function initAddressAutocomplete() {
    if (!CONFIG.gmapsKey) return;
    var groups = [
      { address1: 'address1', city: 'city', province: 'province', zip: 'zip', country: 'country' },
      { address1: 'billing_address1', city: 'billing_city', province: 'billing_province', zip: 'billing_zip', country: 'billing_country' },
      { address1: 'pbill_address1', city: 'pbill_city', province: 'pbill_province', zip: 'pbill_zip', country: 'pbill_country' }
    ];

    function attach() {
      var svc, places;
      try {
        svc = new google.maps.places.AutocompleteService();
        places = new google.maps.places.PlacesService(document.createElement('div'));
      } catch (e) { return; }
      groups.forEach(function (g) {
        var input = root.querySelector('[name="' + g.address1 + '"]');
        if (!input || input.getAttribute('data-ac')) return;
        input.setAttribute('data-ac', '1');
        buildAutocomplete(input, g, svc, places);
      });
    }

    if (window.google && window.google.maps && window.google.maps.places) { attach(); return; }

    (window.__ccGmapsCallbacks = window.__ccGmapsCallbacks || []).push(attach);
    if (!window.__ccGmapsLoading) {
      window.__ccGmapsLoading = true;
      window.__ccGmapsInit = function () {
        (window.__ccGmapsCallbacks || []).forEach(function (fn) { try { fn(); } catch (e) {} });
      };
      var s = document.createElement('script');
      s.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(CONFIG.gmapsKey) + '&libraries=places&callback=__ccGmapsInit&loading=async';
      s.async = true;
      document.head.appendChild(s);
    }
  }

  // -- Custom address suggestions dropdown (Shopify-style) -------------------
  function buildAutocomplete(input, g, svc, places) {
    var fl = input.closest('.cc-fl');
    if (!fl) return;

    var panel = document.createElement('div');
    panel.className = 'cc-ac';
    panel.hidden = true;
    panel.innerHTML = '<div class="cc-ac__head"><span>SUGGESTIONS</span><button type="button" class="cc-ac__close" aria-label="Close">&times;</button></div><div class="cc-ac__list"></div>';
    var list = panel.querySelector('.cc-ac__list');
    fl.appendChild(panel);

    var predictions = [];
    var active = -1;
    var debounce;
    var filling = false;

    function close() { panel.hidden = true; active = -1; }

    function highlight(text, matches) {
      if (!matches || !matches.length) return escapeHtml(text);
      var out = '', last = 0;
      matches.forEach(function (mt) {
        out += escapeHtml(text.slice(last, mt.offset));
        out += '<b>' + escapeHtml(text.slice(mt.offset, mt.offset + mt.length)) + '</b>';
        last = mt.offset + mt.length;
      });
      return out + escapeHtml(text.slice(last));
    }

    function render() {
      list.innerHTML = '';
      predictions.forEach(function (p, i) {
        var item = document.createElement('button');
        item.type = 'button';
        item.className = 'cc-ac__item' + (i === active ? ' is-active' : '');
        item.innerHTML = highlight(p.description, p.matched_substrings);
        item.addEventListener('mousedown', function (e) { e.preventDefault(); choose(p); });
        list.appendChild(item);
      });
    }

    function query(val) {
      // Restrict address suggestions to the country currently selected for THIS
      // address group (shipping / billing / pbill). International store: a UAE
      // customer gets UAE suggestions, a JP customer JP, etc. If the selected
      // country isn't in COUNTRY_CODES, omit the restriction → worldwide
      // suggestions (still works, just unfiltered).
      var opts = { input: val, types: ['address'] };
      var cSel = root.querySelector('[name="' + g.country + '"]');
      var iso = cSel && COUNTRY_CODES[cSel.value];
      if (iso) opts.componentRestrictions = { country: iso };
      svc.getPlacePredictions(opts, function (res, status) {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !res || !res.length) { close(); return; }
        predictions = res; active = -1; render(); panel.hidden = false;
      });
    }

    function choose(p) {
      close();
      places.getDetails({ placeId: p.place_id, fields: ['address_components'] }, function (place, status) {
        if (status === google.maps.places.PlacesServiceStatus.OK && place && place.address_components) {
          filling = true;
          fillFromPlace(place.address_components, g);
          filling = false;
        }
      });
    }

    input.addEventListener('input', function () {
      if (filling) return;
      var val = input.value.trim();
      clearTimeout(debounce);
      if (val.length < 3) { close(); return; }
      debounce = setTimeout(function () { query(val); }, 180);
    });
    input.addEventListener('keydown', function (e) {
      if (panel.hidden) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); active = Math.min(active + 1, predictions.length - 1); render(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); active = Math.max(active - 1, 0); render(); }
      else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); choose(predictions[active]); }
      else if (e.key === 'Escape') { close(); }
    });
    input.addEventListener('blur', function () { setTimeout(close, 150); });
    panel.querySelector('.cc-ac__close').addEventListener('mousedown', function (e) { e.preventDefault(); close(); });
  }

  // The country list is 238 <option>s carrying a data-provinces JSON payload each
  // — about 66 KB of markup. Liquid used to emit it once per address form (ship,
  // billing, PayPal billing): 198 KB, a third of the whole document, to say the
  // same thing three times. Now Liquid renders it once and the other two are
  // cloned here, before linkCountryProvince() reads their options.
  function hydrateCountrySelects() {
    var src = root.querySelector('[name="country"]');
    if (!src || !src.options.length) return;
    root.querySelectorAll('select[data-country-clone]').forEach(function (sel) {
      if (sel.options.length) return;
      sel.innerHTML = src.innerHTML;
      sel.value = ''; // nothing chosen yet — the shipping default must not leak in
    });
  }

  // -- Country → State/Province linking (from country_option_tags) -----------
  function linkCountryProvince(countryField, provinceField) {
    var countrySel = root.querySelector('[name="' + countryField + '"]');
    var provinceSel = root.querySelector('[name="' + provinceField + '"]');
    if (!countrySel || !provinceSel) return;
    var provinceFl = provinceSel.closest('.cc-fl');

    function update() {
      // Nothing chosen yet — leave the field exactly as Liquid rendered it.
      //
      // hydrateCountrySelects() clones the country list into the billing selects
      // and sets value to '' on purpose, so the shipping default does not leak
      // in. update() then found no selected option, read an empty province list,
      // and hid State entirely: the buyer opened "Use a different billing
      // address" and met City and ZIP with no State between them, on a form they
      // had not touched yet. An empty list before a country is picked means
      // "unknown", not "this country has none".
      if (!countrySel.value) {
        if (provinceFl) provinceFl.style.display = '';
        return;
      }
      var opt = countrySel.options[countrySel.selectedIndex];
      var provinces = [];
      try { provinces = JSON.parse((opt && opt.getAttribute('data-provinces')) || '[]'); } catch (e) {}
      provinceSel.innerHTML = '';
      if (!provinces.length) {
        if (provinceFl) provinceFl.style.display = 'none'; // no provinces → hide (e.g. some countries)
        return;
      }
      if (provinceFl) provinceFl.style.display = '';
      var empty = document.createElement('option');
      empty.value = ''; empty.disabled = true; empty.selected = true;
      provinceSel.appendChild(empty);
      provinces.forEach(function (p) {
        var o = document.createElement('option');
        o.value = p[0]; o.textContent = p[0];
        provinceSel.appendChild(o);
      });
      if (provinceFl) provinceFl.classList.remove('cc-fl--filled'); // nothing chosen yet
      provinceSel.dispatchEvent(new Event('change', { bubbles: true })); // sync enhanced trigger
    }
    // Auto-detect: pre-select the visitor's country (Shopify Markets geolocation)
    // for the SHIPPING country only. If that country isn't a shippable option
    // (not in country_option_tags), fall back to the USA default. Runs before
    // enhanceSelect + the updateShippingTax listeners are wired, so it won't
    // fire a premature quote request.
    if (countryField === 'country' && CONFIG.geoCountry) {
      var geo = CONFIG.geoCountry, target = '';
      for (var gi = 0; gi < countrySel.options.length; gi++) {
        if (countrySel.options[gi].value === geo) { target = geo; break; }
      }
      if (!target) {
        for (var ui = 0; ui < countrySel.options.length; ui++) {
          if (countrySel.options[ui].value === 'United States') { target = 'United States'; break; }
        }
      }
      if (target && countrySel.value !== target) countrySel.value = target;
    }

    countrySel.addEventListener('change', update);
    update();
  }

  // -- Custom glass dropdown (styled, blurred option list) -------------------
  function enhanceSelect(sel) {
    var fl = sel.closest('.cc-fl');
    if (!fl || sel.getAttribute('data-enhanced')) return;
    sel.setAttribute('data-enhanced', '1');

    var dd = document.createElement('div');
    dd.className = 'cc-dd';
    var trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'cc-dd__trigger';
    trigger.innerHTML = '<span class="cc-dd__value"></span><svg class="cc-dd__chev" width="12" height="8" viewBox="0 0 12 8" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M1 1l5 5 5-5"/></svg>';
    var valueSpan = trigger.querySelector('.cc-dd__value');

    var panel = document.createElement('div');
    panel.className = 'cc-dd__panel';
    panel.hidden = true;
    panel.innerHTML = '<div class="cc-dd__search"><input type="text" class="cc-dd__searchinput" placeholder="Search" autocomplete="off"></div><div class="cc-dd__list"></div>';
    var searchWrap = panel.querySelector('.cc-dd__search');
    var searchInput = panel.querySelector('.cc-dd__searchinput');
    var list = panel.querySelector('.cc-dd__list');

    function realOptions() {
      return Array.prototype.filter.call(sel.options, function (o) { return !(o.disabled && !o.value); });
    }
    function updateTrigger() {
      var o = sel.options[sel.selectedIndex];
      valueSpan.textContent = (o && o.value) ? o.textContent : '';
    }
    function buildList(filter) {
      list.innerHTML = '';
      filter = (filter || '').toLowerCase();
      realOptions().forEach(function (opt) {
        if (filter && opt.textContent.toLowerCase().indexOf(filter) < 0) return;
        var item = document.createElement('button');
        item.type = 'button';
        item.className = 'cc-dd__opt' + (opt.selected ? ' is-selected' : '');
        var t = document.createElement('span');
        t.textContent = opt.textContent;
        item.appendChild(t);
        if (opt.selected) {
          var chk = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          chk.setAttribute('class', 'cc-dd__check'); chk.setAttribute('width', '16'); chk.setAttribute('height', '16');
          chk.setAttribute('viewBox', '0 0 24 24'); chk.setAttribute('fill', 'none'); chk.setAttribute('stroke', 'currentColor'); chk.setAttribute('stroke-width', '2');
          chk.innerHTML = '<path d="M5 12l5 5L20 7"/>';
          item.appendChild(chk);
        }
        item.addEventListener('click', function () {
          sel.value = opt.value;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
          updateTrigger();
          close();
        });
        list.appendChild(item);
      });
    }
    function open() {
      buildList('');
      searchWrap.style.display = 'none';
      panel.hidden = false;
      dd.classList.add('is-open');
      if (searchInput) { searchInput.value = ''; setTimeout(function () { searchInput.focus(); }, 0); }
      setTimeout(function () { document.addEventListener('click', outside, true); }, 0);
    }
    function close() {
      panel.hidden = true;
      dd.classList.remove('is-open');
      document.removeEventListener('click', outside, true);
    }
    function outside(e) { if (!dd.contains(e.target)) close(); }

    trigger.addEventListener('click', function () { panel.hidden ? open() : close(); });
    if (searchInput) searchInput.addEventListener('input', function () { buildList(searchInput.value); });
    sel.addEventListener('change', updateTrigger);

    dd.appendChild(trigger);
    dd.appendChild(panel);
    fl.appendChild(dd);
    sel.classList.add('cc-dd-native');
    updateTrigger();
  }

  // -- Shipping method + total — REAL rates from the storefront (no mock).
  //    /cart/shipping_rates.json reads the store's actual shipping profiles /
  //    zones for the entered address. (Tax needs the backend draft order, so it
  //    is intentionally NOT shown here — never display a value we can't verify.)
  function subtotalCents() {
    return state.cart ? (state.cart.items_subtotal_price || state.cart.total_price || 0) : 0;
  }
  function applyShipping(priceDollars) {
    var shipCents = Math.round((parseFloat(priceDollars) || 0) * 100);
    setText('[data-summary-shipping]', shipCents > 0 ? money(shipCents / 100) : 'Free');
    var total = money((subtotalCents() + shipCents) / 100);
    setText('[data-summary-total]', total);
    setText('[data-summary-bar-total]', total);
  }
  function resetShipping(msg) {
    var note = $('[data-shipping-note]');
    if (note) { note.className = 'cc-note'; note.textContent = msg || 'Enter your shipping address to view available shipping methods.'; }
    setText('[data-summary-shipping]', 'Enter shipping address');
    hideSel('[data-summary-tax-row]');
    hideSel('[data-summary-discount-row]');
    hideSel('[data-summary-savings-row]');
    var subMoney = money(subtotalCents() / 100);
    setText('[data-summary-total]', subMoney);
    setText('[data-summary-bar-total]', subMoney);
    syncRecap();
  }
  // Full address (province + zip + country) unlocks live shipping rates + tax.
  function addressComplete() {
    return ['province', 'zip', 'country'].every(function (n) { return (val(n) || '').length > 0; });
  }
  function renderShippingRates(rates) {
    var note = $('[data-shipping-note]');
    if (!note) return;
    if (!rates || !rates.length) { resetShipping('No shipping methods available for this address.'); return; }
    note.className = 'cc-pay cc-pay--ship';
    var html = '';
    rates.forEach(function (r, i) {
      var priceNum = parseFloat(r.price) || 0;
      var label = priceNum > 0 ? money(priceNum) : 'FREE';
      var est = '';
      var dd = r.delivery_days;
      // Every sofa is made to order (5 to 7 weeks). The rate's transit days read as the
      // total wait when printed here, so show the real promise instead.
      if (dd && dd.length) est = 'Made to order, arrives in 5 to 7 weeks';
      html += '<div class="cc-pay__method' + (i > 0 ? ' cc-pay__method--sep' : '') + '">' +
        '<label class="cc-pay__row cc-pay__row--ship">' +
        '<span class="cc-pay__label"><input type="radio" name="shipmethod" ' + (i === 0 ? 'checked' : '') + ' data-price="' + priceNum + '">' +
        '<span class="cc-ship-opt__txt"><span class="cc-ship-opt__name">' + escapeHtml(r.name) + '</span>' +
        (est ? '<span class="cc-ship-opt__est">' + escapeHtml(est) + '</span>' : '') + '</span></span>' +
        '<span class="cc-ship-opt__price">' + label + '</span></label></div>';
    });
    note.innerHTML = html;
    note.querySelectorAll('input[name="shipmethod"]').forEach(function (radio) {
      radio.addEventListener('change', function () { applyShipping(radio.getAttribute('data-price')); });
    });
    applyShipping(rates[0].price);
  }
  var shipReq = 0, shipDebounce;

  // Dispatcher: with a backend configured, use the authoritative quote
  // (real subtotal/tax/shipping/discount). Otherwise fall back to storefront
  // shipping rates (shipping only) so the preview still works standalone.
  function updateShippingTax() {
    if (state.draftMode) return; // draft-order totals are LOCKED — never re-quote
    if (CONFIG.backendUrl) return updateQuote();
    return updateShippingTaxStorefront();
  }

  function updateShippingTaxStorefront() {
    clearTimeout(shipDebounce);
    var filled = ['address1', 'city', 'province', 'zip', 'country'].every(function (n) { return (val(n) || '').length > 0; });
    if (!filled) { resetShipping(); return; }
    var note = $('[data-shipping-note]');
    if (note) { note.className = 'cc-note'; note.textContent = 'Loading shipping methods…'; }
    shipDebounce = setTimeout(function () {
      var params = 'shipping_address[country]=' + encodeURIComponent(val('country')) +
                   '&shipping_address[province]=' + encodeURIComponent(val('province')) +
                   '&shipping_address[zip]=' + encodeURIComponent(val('zip'));
      var myReq = ++shipReq;
      fetch('/cart/shipping_rates.json?' + params, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (data) { if (myReq === shipReq) renderShippingRates(data && data.shipping_rates); })
        .catch(function () { if (myReq === shipReq) resetShipping('Could not load shipping methods.'); });
    }, 400);
  }

  // -- Backend quote: authoritative subtotal / tax / shipping / discount -----
  // Store credit redeemed in the cart (cart attribute _credit_redeem_amount),
  // capped like the app's discount function: min(requested, 50% of subtotal).
  function computeStoreCredit() {
    var attrs = (state.cart && state.cart.attributes) || {};
    var requested = parseFloat(attrs._credit_redeem_amount || 0);
    if (!(requested > 0)) return 0;
    var subCents = (state.cart && state.cart.items_subtotal_price) || 0;
    var cap = 0.5 * (subCents / 100);
    var amount = Math.min(requested, cap);
    return amount > 0 ? Math.round(amount * 100) / 100 : 0;
  }

  function quotePayload() {
    return {
      cart: { items: ((state.cart && state.cart.items) || []).map(function (i) {
        return { variantId: i.variant_id, quantity: i.quantity };
      }) },
      shippingAddress: {
        address1: val('address1'), city: val('city'),
        province: val('province'), country: val('country'), zip: val('zip')
      },
      discountCodes: state.discountCodes.slice(),
      storeCredit: computeStoreCredit(),
      shippingRateHandle: state.shipHandle || null
    };
  }

  function updateQuote() {
    clearTimeout(shipDebounce);
    // Quote when it can produce something useful: a full address (shipping + tax)
    // OR a discount code to validate. A code alone doesn't need an address.
    if (!state.cart || (!addressComplete() && !state.discountCodes.length)) { resetShipping(); return; }
    var note = $('[data-shipping-note]');
    if (note && note.className === 'cc-note') {
      note.textContent = addressComplete()
        ? 'Loading shipping methods…'
        : 'Enter your shipping address to view available shipping methods.';
    }
    shipDebounce = setTimeout(function () {
      var myReq = ++shipReq;
      var sentHandle = state.shipHandle || null;
      fetch(ENDPOINTS.quote, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(quotePayload())
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (myReq !== shipReq) return;
          if (data.error) throw new Error(data.error);
          renderQuoteRates(data.availableShippingRates);
          renderQuoteTotals(data.totals);
          reflectDiscount(data);
          // First pass carried no shipping selection → re-quote with the
          // auto-picked rate so the total includes shipping (Shopify-authoritative).
          if (!sentHandle && state.shipHandle) updateQuote();
        })
        .catch(function (e) {
          if (myReq !== shipReq) return;
          if (window.console) console.error('[cc] quote failed:', e, 'url:', ENDPOINTS.quote);
          resetShipping('Quote error: ' + ((e && e.message) || e));
          clearPromoLoading();
        });
    }, 400);
  }

  function renderQuoteRates(rates) {
    var note = $('[data-shipping-note]');
    if (!note) return;
    if (!rates || !rates.length) {
      note.className = 'cc-note';
      note.textContent = addressComplete()
        ? 'No shipping methods available for this address.'
        : 'Enter your shipping address to view available shipping methods.';
      state.shipHandle = null;
      return;
    }
    // Preserve the customer's chosen method across re-quotes (match by title —
    // Shopify re-issues rate handles on each calculate).
    var selIdx = 0;
    for (var k = 0; k < rates.length; k++) { if (rates[k].title === state.shipTitle) { selIdx = k; break; } }
    note.className = 'cc-pay cc-pay--ship';
    var html = '';
    rates.forEach(function (r, i) {
      var priceNum = parseFloat(r.price) || 0;
      var label = priceNum > 0 ? money(priceNum) : 'FREE';
      html += '<div class="cc-pay__method' + (i > 0 ? ' cc-pay__method--sep' : '') + '">' +
        '<label class="cc-pay__row cc-pay__row--ship">' +
        '<span class="cc-pay__label"><input type="radio" name="shipmethod" ' + (i === selIdx ? 'checked' : '') + ' data-idx="' + i + '">' +
        '<span class="cc-ship-opt__txt"><span class="cc-ship-opt__name">' + escapeHtml(r.title) + '</span></span></span>' +
        '<span class="cc-ship-opt__price">' + label + '</span></label></div>';
    });
    note.innerHTML = html;
    state.shipHandle = rates[selIdx].handle;
    state.shipTitle = rates[selIdx].title;
    note.querySelectorAll('input[name="shipmethod"]').forEach(function (radio) {
      radio.addEventListener('change', function () {
        var r = rates[parseInt(radio.getAttribute('data-idx'), 10)];
        if (!r) return;
        state.shipHandle = r.handle; state.shipTitle = r.title;
        updateQuote();
      });
    });
  }

  function renderQuoteTotals(t) {
    if (!t) return;
    // Keep the authoritative totals on state so express paths (Apple Pay) can read
    // the amount before create-order runs.
    state.totals = t;
    // The Stripe Element shows this amount in the Apple Pay sheet and Affirm's
    // instalment preview, so it follows the authoritative total.
    syncStripeAmount();
    // Totals changed → any previously-created order/draft is stale. Drop it so the
    // next payment re-creates it at the current amount (never charges a stale
    // total). Skipped while a payment is in flight so a late quote can't null the
    // ids mid-capture (card sets `submitting`, PayPal sets `ppInFlight`).
    if (!state.submitting && !state.ppInFlight) {
      state.paypalOrderId = null;
      state.draftOrderId = null;
    }
    setText('[data-summary-subtotal]', money(t.subtotal));
    // t.discount = ALL order discounts (coupons + store credit). Split them so the
    // summary shows "Order discount" (coupons) and "Store Credit" on separate lines.
    var disc = parseFloat(t.discount) || 0;
    var sc = computeStoreCredit();
    var couponDisc = Math.max(0, Math.round((disc - sc) * 100) / 100);
    if (couponDisc > 0) {
      setText('[data-summary-discount]', '−' + money(couponDisc));
      setDiscountCode(state.discountCodes);
      showSel('[data-summary-discount-row]');
    } else {
      hideSel('[data-summary-discount-row]');
    }
    if (sc > 0) {
      setText('[data-summary-storecredit]', '−' + money(sc));
      showSel('[data-summary-storecredit-row]');
    } else {
      hideSel('[data-summary-storecredit-row]');
    }
    if (disc > 0) {
      setText('[data-summary-savings]', money(disc));
      showSel('[data-summary-savings-row]');
    } else {
      hideSel('[data-summary-savings-row]');
    }
    var ship = parseFloat(t.shipping) || 0;
    setText('[data-summary-shipping]', ship > 0 ? money(ship) : 'Free');
    var tax = parseFloat(t.tax) || 0;
    if (tax > 0) { setText('[data-summary-tax]', money(tax)); showSel('[data-summary-tax-row]'); }
    else { hideSel('[data-summary-tax-row]'); }
    setText('[data-summary-total]', money(t.total));
    setText('[data-summary-bar-total]', money(t.total));
    syncRecap();
  }

  // Keep the mobile recap (above Pay now) in sync with the order summary.
  function syncRecap() {
    var t = $('[data-summary-total]');
    if (t) setText('[data-mrecap-total]', t.textContent);
    var savRow = $('[data-summary-savings-row]');
    var rSavRow = $('[data-mrecap-savings-row]');
    if (savRow && rSavRow) {
      var hid = savRow.hasAttribute('hidden');
      rSavRow.hidden = hid;
      var s = $('[data-summary-savings]');
      if (!hid && s) setText('[data-mrecap-savings]', s.textContent);
    }
  }
  // The mobile recap and the top summary share one open/close state, so the
  // recap acts as a second accordion handle for the same summary.
  function setTopSummary(open, scroll) {
    var panel = $('.cc-sum');
    var bar = root.querySelector('[data-summary-toggle]');
    if (panel) panel.classList.toggle('is-open', open);
    if (bar) bar.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open && scroll) {
      var right = $('.cc-right');
      if (right && right.scrollIntoView) right.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
  // The mobile recap is an INDEPENDENT second summary (expands its own mirror).
  function toggleRecap() {
    var recap = root.querySelector('[data-mobile-recap]');
    if (recap) recap.classList.toggle('is-open');
  }
  function toggleTopSummary() {
    var panel = $('.cc-sum');
    setTopSummary(panel ? !panel.classList.contains('is-open') : true, true);
  }
  function openTopSummary() { setTopSummary(true, true); }

  // Applied discount shown Shopify-style: a removable tag chip below the field.
  var TAG_SVG = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1"><g stroke-linejoin="round"><path stroke-linecap="round" d="M10.5 1.5H7.93a2.5 2.5 0 0 0-1.8.766L1.52 7.052a1.5 1.5 0 0 0 .02 2.101l3.48 3.48a1.25 1.25 0 0 0 1.75.016l5.116-4.926a2 2 0 0 0 .613-1.441V3.5a2 2 0 0 0-2-2"/><circle cx="9.5" cy="4.5" r=".563" stroke-linecap="round"/><path d="M9.49 4.49h.02v.02h-.02z"/></g></svg>';
  var X_SVG = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M18 6L6 18M6 6l12 12"/></svg>';

  function setPromoError(text) {
    var el = root.querySelector('[data-promo-msg]');
    if (!el) return;
    el.textContent = text || '';
    el.hidden = !text;
  }
  // A pay link cannot change its own price, so the code field is shown disabled
  // rather than inviting input that could not take effect. Called once the draft
  // has loaded; the ordinary cart checkout never reaches it.
  function lockPromoField() {
    var wrap = root.querySelector('.cc-promo');
    if (wrap) wrap.classList.add('cc-promo--locked');
    var input = root.querySelector('[name="discount"]');
    if (input) input.disabled = true;
    var btn = root.querySelector('[data-apply-promo]');
    if (btn) btn.disabled = true;
  }

  function renderPromoTag() {
    var box = root.querySelector('[data-promo-tags]');
    if (!box) return;
    if (state.discountCodes && state.discountCodes.length) {
      box.innerHTML = state.discountCodes.map(function (code) {
        // No remove button on a pay link. The draft prices the order server-side
        // — create-stripe-for-draft charges draft.totals.total and never reads
        // anything the browser sends — so an × here would appear to drop the
        // discount while the amount charged stayed exactly the same. A control
        // that lies is worse than no control.
        var x = DRAFT ? '' :
          '<button type="button" data-remove-promo="' + escapeHtml(code) + '" aria-label="Remove discount">' + X_SVG + '</button>';
        return '<span class="cc-promo-tag">' + TAG_SVG +
          '<span>' + escapeHtml(code.toUpperCase()) + '</span>' + x + '</span>';
      }).join('');
      box.hidden = false;
      box.querySelectorAll('[data-remove-promo]').forEach(function (btn) {
        btn.addEventListener('click', function () { removeDiscount(btn.getAttribute('data-remove-promo')); });
      });
    } else {
      box.innerHTML = '';
      box.hidden = true;
    }
  }
  // Remove one code (by value) or all codes when called with no argument.
  function removeDiscount(code) {
    if (code) {
      state.discountCodes = state.discountCodes.filter(function (c) {
        return c.toLowerCase() !== String(code).toLowerCase();
      });
    } else {
      state.discountCodes = [];
    }
    state.pendingCode = null;
    var input = root.querySelector('[name="discount"]');
    if (input) input.value = '';
    setPromoError('');
    renderPromoTag();
    updateShippingTax();
  }
  function clearPromoLoading() {
    var b = root.querySelector('[data-apply-promo]');
    if (b) b.classList.remove('is-loading');
  }
  // Backend returns the codes Shopify actually applied (appliedDiscountCodes) —
  // that is the source of truth. Codes that don't combine/aren't eligible are
  // silently skipped by Shopify, so any pending code missing from that list failed.
  function reflectDiscount(data) {
    var applied = (data.appliedDiscountCodes || []).slice();
    state.discountCodes = applied;
    if (state.pendingCode) {
      var stuck = applied.some(function (c) {
        return c.toLowerCase() === state.pendingCode.toLowerCase();
      });
      var input = root.querySelector('[name="discount"]');
      if (stuck) {
        if (input) input.value = '';    // clear field; the chip shows the code instead
        setPromoError('');
      } else {
        setPromoError('“' + state.pendingCode.toUpperCase() + '” can’t be applied or combined with your other codes');
      }
      state.pendingCode = null;
    }
    renderPromoTag();
    clearPromoLoading();
  }

  // -- Phone: country flag (SVG) + US formatting ----------------------------
  var COUNTRY_CODES = {
    'Austria': 'at', 'Belgium': 'be', 'Czechia': 'cz', 'Denmark': 'dk', 'Finland': 'fi', 'France': 'fr',
    'Germany': 'de', 'Ireland': 'ie', 'Netherlands': 'nl', 'Norway': 'no', 'Poland': 'pl', 'Portugal': 'pt',
    'Spain': 'es', 'Sweden': 'se', 'Switzerland': 'ch', 'United Arab Emirates': 'ae', 'United Kingdom': 'gb',
    'United States': 'us', 'Canada': 'ca', 'Australia': 'au', 'Italy': 'it',
    // Common international shipping destinations (name must match Shopify's spelling;
    // any miss just falls back to unrestricted worldwide suggestions).
    'Japan': 'jp', 'China': 'cn', 'Hong Kong SAR': 'hk', 'Singapore': 'sg', 'South Korea': 'kr',
    'Taiwan': 'tw', 'India': 'in', 'Saudi Arabia': 'sa', 'Qatar': 'qa', 'Kuwait': 'kw', 'Bahrain': 'bh',
    'Oman': 'om', 'Israel': 'il', 'Mexico': 'mx', 'Brazil': 'br', 'New Zealand': 'nz', 'South Africa': 'za',
    'Greece': 'gr', 'Luxembourg': 'lu', 'Monaco': 'mc', 'Malaysia': 'my', 'Thailand': 'th', 'Indonesia': 'id',
    'Philippines': 'ph', 'Vietnam': 'vn', 'Turkey': 'tr'
  };

  function initPhone() {
    var phoneInput = root.querySelector('[name="phone"]');
    var countrySel = root.querySelector('[name="country"]');
    if (!phoneInput) return;
    function code() { return (countrySel && COUNTRY_CODES[countrySel.value]) || 'us'; }
    function updateFlag() {
      var wrap = root.querySelector('[data-phone-flag]');
      var img = wrap && wrap.querySelector('img');
      if (img) img.src = 'https://flagcdn.com/' + code() + '.svg';
    }
    phoneInput.addEventListener('input', function () {
      if (code() !== 'us') return;
      var d = phoneInput.value.replace(/\D/g, '');
      if (d.length > 10 && d.charAt(0) === '1') d = d.slice(1);
      d = d.slice(0, 10);
      var out = '';
      if (d.length > 6) out = '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6);
      else if (d.length > 3) out = '(' + d.slice(0, 3) + ') ' + d.slice(3);
      else if (d.length > 0) out = '(' + d;
      phoneInput.value = out;
    });
    if (countrySel) countrySel.addEventListener('change', updateFlag);
    updateFlag();
  }

  // -- boot ------------------------------------------------------------------
  // -- Draft-order pay mode: render + prefill from the admin's locked draft ----
  function renderDraftItems(items) {
    var list = $('[data-cart-items]');
    if (!list || !items) return;
    list.innerHTML = '';
    items.forEach(function (it) {
      var row = document.createElement('div');
      row.className = 'cc-line';
      row.innerHTML =
        '<div class="cc-line__media">' +
          (it.image ? '<img src="' + it.image + '" alt="">' : '') +
          '<span class="cc-line__qty">' + (it.quantity || 1) + '</span>' +
        '</div>' +
        '<div class="cc-line__body">' +
          '<div class="cc-line__info">' +
            '<div class="cc-line__title">' + escapeHtml(it.title || '') + '</div>' +
            (it.variantTitle ? '<div class="cc-line__variant">' + escapeHtml(it.variantTitle) + '</div>' : '') +
          '</div>' +
          '<div class="cc-line__price">' + money(it.lineTotal) + '</div>' +
        '</div>';
      list.appendChild(row);
    });
    list.setAttribute('data-server-rendered', '1'); // never rebuild from a cart

    // The subtotal label is server-rendered by Liquid from cart.item_count — the
    // VISITOR's own cart, which has nothing to do with the invoice they were
    // sent. On a pay link nothing ever overwrote it: renderTotals() rewrites the
    // amount but not the label, and loadCart() never runs in draft mode. So a
    // buyer holding three things in their cart read "Subtotal · 3 items" above a
    // one-item invoice, and someone with an empty cart read it correctly by
    // luck. Corrected here, where the draft's own lines are known.
    //
    // Counted in units rather than lines, because cart.item_count counts
    // quantities — the label has to mean the same thing whichever mode wrote it.
    var units = items.reduce(function (n, it) { return n + (it.quantity || 1); }, 0);
    setText('[data-summary-subtotal-label]', 'Subtotal' + (units > 1 ? ' \u00b7 ' + units + ' items' : ''));
    setText('[data-mrecap-items]', units + (units === 1 ? ' item' : ' items'));
  }

  /**
   * The shipping box on a pay link.
   *
   * updateShippingTax() returns early in draft mode — the totals are locked, so
   * nothing re-quotes and nothing ever rewrote this note. It therefore kept the
   * Liquid placeholder, "Enter your shipping address to view available shipping
   * methods", on a page whose address is already filled in and whose shipping was
   * decided when the invoice was raised. The line was untrue and unactionable:
   * no address the buyer could type would ever make a method appear.
   *
   * Rendered as one checked row in renderShippingRates' exact markup — same
   * cc-pay--ship container, same label/radio/price structure, same uppercase
   * FREE — so a pay link and an ordinary checkout look like one product rather
   * than two. It is the only row and it is already checked, so clicking it emits
   * no change event; deliberately no listener and no applyShipping() call, since
   * re-pricing is the one thing a locked draft must never do.
   *
   * The method name needs a backend that sends shippingMethod; without one the
   * row still states the price, so an undeployed backend softens the label
   * rather than restoring the falsehood.
   */
  function renderDraftShipping(totals, method) {
    var note = $('[data-shipping-note]');
    if (!note) return;
    var priceNum = parseFloat(totals && totals.shipping) || 0;
    var label = priceNum > 0 ? money(priceNum) : 'FREE';
    note.className = 'cc-pay cc-pay--ship';
    note.innerHTML =
      '<div class="cc-pay__method">' +
        '<label class="cc-pay__row cc-pay__row--ship">' +
          '<span class="cc-pay__label"><input type="radio" name="shipmethod" checked data-price="' + priceNum + '">' +
            '<span class="cc-ship-opt__txt"><span class="cc-ship-opt__name">' +
              escapeHtml(method || 'Shipping') +
            '</span></span>' +
          '</span>' +
          '<span class="cc-ship-opt__price">' + label + '</span>' +
        '</label>' +
      '</div>';
  }

  function prefillFromDraft(d) {
    var a = d.shippingAddress || {};
    var setVal = function (n, v) {
      var el = root.querySelector('[name="' + n + '"]');
      if (el && v != null && v !== '') {
        el.value = v;
        var fl = el.closest('.cc-fl');
        if (fl) fl.classList.add('cc-fl--filled');
      }
    };
    if (d.email) setVal('email', d.email);
    setVal('first_name', a.firstName); setVal('last_name', a.lastName);
    setVal('address1', a.address1); setVal('address2', a.address2);
    setVal('city', a.city); setVal('zip', a.zip); setVal('phone', a.phone);

    // Country FIRST, and its change dispatched before province is written.
    //
    // Province used to be set alongside city, with both changes fired afterwards
    // in one loop. That never survived: the loop walks document order, so
    // country fired first, and linkCountryProvince's update() rebuilds the
    // province <select> from that country's data-provinces and selects a blank
    // placeholder — wiping the province written moments earlier. Draft #D137
    // prefilled every field except State, and because shipping rates need a
    // complete address the buyer met "Enter your shipping address to view
    // available shipping methods" with no visible reason why. On a pay link for
    // a $2,698 sofa that reads as a broken page, not a field to fill in.
    var countrySel = root.querySelector('[name="country"]');
    if (countrySel && a.country) {
      countrySel.value = a.country;
      var countryFl = countrySel.closest('.cc-fl');
      if (countryFl) countryFl.classList.add('cc-fl--filled');
      // Rebuilds the province options for this country, so the value below has
      // something to match against.
      countrySel.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Shopify's country_option_tags and the backend both speak full names
    // ("United States", "South Carolina"), which is what update() writes into
    // option.value — so these match without any code lookup.
    setVal('province', a.province);
    var provinceSel = root.querySelector('[name="province"]');
    // Syncs the enhanced select's visible trigger, which does not observe .value.
    if (provinceSel) provinceSel.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function loadDraftMode() {
    setStatus('Loading your order…', 'info');
    return fetch(ENDPOINTS.loadDraft, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ draftId: DRAFT.id, token: DRAFT.token })
    })
      .then(handleJson)
      .then(function (d) {
        if (d.alreadyPaid) {
          setStatus('This order has already been paid. Thank you!', 'info');
          root.querySelectorAll('[data-pay-button], [data-continue-button]').forEach(function (b) { b.disabled = true; });
          return d;
        }
        renderDraftItems(d.lineItems);
        prefillFromDraft(d);
        // Before renderTotals, which reads this to label the discount row. A pay
        // link has no cart, so the draft is the only place the applied code can
        // come from — without it the row rendered its tag icon with nothing
        // beside it, which is what a buyer saw on every emailed invoice.
        if (Array.isArray(d.discountCodes)) state.discountCodes = d.discountCodes.slice();
        // Show the code the buyer already applied on their earlier attempt, and
        // lock the field: on this page the price is the draft's, not the cart's.
        renderPromoTag();
        lockPromoField();
        state.totals = d.totals;
        renderTotals(d.totals, null);
        renderDraftShipping(d.totals, d.shippingMethod);
        // The pay-link path has no cart and never quotes, so neither of the two
        // places that normally call this is reached — and this is the only thing
        // that builds Apple Pay, which Stripe refuses to create while the total
        // is still zero. Without it a payment link simply never offered Apple
        // Pay, silently, while the ordinary checkout did.
        syncStripeAmount();
        setStatus('', null);
        return d;
      })
      .catch(function (err) {
        setStatus(friendlyError(err, 'This payment link is invalid or has expired. Please contact us for a new link.'), 'error');
        throw err;
      });
  }

  function init() {
    // Meta/tab title: "<page> | Sofatica®" (applies to checkout, thank-you & draft states).
    try { document.title = 'Secure Checkout | Sofatica®'; } catch (e) {}
    // Preview the thank-you screen without paying (?cc_ty_preview=1). Allowed on
    // localhost and on sofatica.com (harmless: no order, no payment, placeholder
    // data only; only fires on the explicit query param).
    if (/[?&]cc_ty_preview\b/.test(location.search) &&
        (/^(localhost|127\.0\.0\.1)$/.test(location.hostname) || /(^|\.)sofatica\.com$/.test(location.hostname))) {
      var setVal = function (n, v) { var el = root.querySelector('[name="' + n + '"]'); if (el) el.value = v; };
      // Generic placeholder data (no real customer PII).
      setVal('first_name', 'Jane'); setVal('last_name', 'Doe'); setVal('email', 'jane.doe@example.com');
      setVal('address1', '123 Sample Street'); setVal('city', 'Springfield');
      setVal('province', 'Illinois'); setVal('zip', '62704'); setVal('country', 'United States');
      setVal('phone', '(555) 010-1234');
      state.shipTitle = 'FedEx Home Delivery®';
      var itemsBox = root.querySelector('.cc-sum [data-cart-items]');
      if (itemsBox) {
        itemsBox.innerHTML = '<div class="cc-line">'
          + '<div class="cc-line__media"><span class="cc-line__qty">1</span></div>'
          + '<div class="cc-line__body">'
          + '<div class="cc-line__info"><div class="cc-line__title">Sample Product</div>'
          + '<div class="cc-line__variant">Variant</div></div>'
          + '<div class="cc-line__price">$1,259.00</div>'
          + '</div></div>';
      }
      // Drive the summary through the SAME authoritative path as a real order
      // (renderTotals via the safety net), then stop init so loadCart's empty-cart
      // render can't zero it out.
      state.totals = { subtotal: '1259.00', discount: '0.00', shipping: '199.00', tax: '110.17', total: '1568.17', currency: 'USD' };
      state.paymentMethod = { type: 'card', brand: 'VISA', last4: '4242' };
      showThankYou({ name: 'SF1001', statusUrl: '' });
      // Keep initialising (binds the summary accordion etc.), but skip loadCart
      // below so the empty cart doesn't overwrite the mocked summary.
      state.previewMode = true;
    }
    if (DRAFT) state.draftMode = true; // pay-an-existing-draft mode (locked totals)
    hydrateCountrySelects(); // must precede linkCountryProvince — it reads the options
    initAddressAutocomplete();
    initPhone();
    linkCountryProvince('country', 'province');
    linkCountryProvince('billing_country', 'billing_province');
    linkCountryProvince('pbill_country', 'pbill_province');
    ['country', 'province', 'billing_country', 'billing_province', 'pbill_country', 'pbill_province'].forEach(function (n) {
      var s = root.querySelector('[name="' + n + '"]');
      if (s) enhanceSelect(s);
    });
    ['address1', 'city', 'province', 'zip', 'country'].forEach(function (n) {
      var el = root.querySelector('[name="' + n + '"]');
      if (el) { el.addEventListener('input', updateShippingTax); el.addEventListener('change', updateShippingTax); }
    });

    // Discount code: "Apply" (or Enter) re-quotes with the code so Shopify
    // validates + prices it. Feedback is shown by reflectDiscount().
    var promoInput = root.querySelector('[name="discount"]');
    var applyPromoBtn = root.querySelector('[data-apply-promo]');
    function applyPromo() {
      var code = ((promoInput && promoInput.value) || '').trim();
      if (!code) return;
      // Stack codes: add to the list (dedupe, case-insensitive) and re-quote so
      // Shopify validates + combines them. pendingCode lets us flag one that fails.
      var exists = state.discountCodes.some(function (c) { return c.toLowerCase() === code.toLowerCase(); });
      if (!exists) state.discountCodes.push(code);
      state.pendingCode = code;
      if (applyPromoBtn) applyPromoBtn.classList.add('is-loading');
      updateShippingTax();
    }
    if (applyPromoBtn) applyPromoBtn.addEventListener('click', applyPromo);
    if (promoInput) promoInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); applyPromo(); }
    });
    if (CONFIG.testMode) {
      var banner = $('[data-test-banner]');
      if (banner) show(banner);
    }
    // A buyer returning from Affirm may already have paid. Settle that before the
    // page can put a payment form in front of them again.
    if (isStripeCard()) {
      armBfcacheRecovery();
      // Runs before the rails mount: neither of these needs the SDK, and the
      // payment list should be right from the first paint rather than after a
      // round trip that may fail.
      affirmRowSync(); afterpayRowSync();
      previewStripeRows();
      resumeStripeRedirect();
    }

    if (state.draftMode) {
      // Draft-order pay link: load the locked draft (items + totals + prefill),
      // then mount the same payment rails. No cart, no profile-check, no re-quote.
      loadDraftMode();
      mountPaymentRails();
    } else if (!state.previewMode) {
      // Both start now and neither waits for the other: the payment form is the
      // slowest thing on the page and it does not need a single byte of the cart.
      mountPaymentRails();
      loadCart()
        .then(function (cart) {
          if (!cart.items || cart.items.length === 0) {
            setCartStatus('Your cart is empty — add items before completing a purchase.', 'info');
            // Keep the form visible so the layout can still be previewed.
          } else {
            publishCheckoutStarted();
          }
          // First real amount the Element can show, until a quote replaces it.
          syncStripeAmount();
          // NOTE: no mixed-profile → native routing on Enigwatch. Native checkout
          // has no payment gateway, so every cart (incl. multi-profile) is priced
          // here as a single draft order. checkCartProfiles() is intentionally not called.
        })
        .catch(function (err) {
          // Scoped to the cart now — the payment form is up regardless, so this must
          // not read as "checkout is broken".
          if (window.console) console.error('[cc] cart failed to load:', err);
          setCartStatus(friendlyError(err, 'We could not load your cart. Please refresh the page — your items are safe.'), 'error');
        });
    }

    var continueBtn = $('[data-continue-button]');
    if (continueBtn) continueBtn.addEventListener('click', function () { createOrder(); });

    var payBtn = $('[data-pay-button]');
    if (payBtn) payBtn.addEventListener('click', pay);

    // Hide the shared "Pay now" button when PayPal (its own button) is selected.
    root.querySelectorAll('[name="paymethod"]').forEach(function (r) {
      r.addEventListener('change', syncPayCta);
    });
    syncPayCta();

    // Clear a field's error state as soon as the customer starts fixing it.
    root.addEventListener('input', function (e) {
      var el = e.target;
      if (el && el.hasAttribute && el.hasAttribute('required')) markField(el, false);
    });
    root.addEventListener('change', function (e) {
      var el = e.target;
      if (el && el.hasAttribute && el.hasAttribute('required') && el.value && el.value.trim()) markField(el, false);
    });

    // Billing: "Use shipping address as billing address" — unchecking reveals the form.
    var billingToggle = root.querySelector('[data-billing-toggle]');
    var billingForm = $('[data-billing-form]');
    if (billingToggle && billingForm) {
      billingToggle.addEventListener('change', function () {
        billingForm.hidden = billingToggle.checked;
      });
    }

    // Policy modals (footer links → pop-up).
    //
    // The four policy bodies were inlined into every checkout load — privacy and
    // terms alone are 61 KB — to serve a modal most customers never open. They are
    // fetched from the store's own /policies/… page on first open instead, and
    // kept once fetched. Anything that goes wrong falls back to a plain link, so
    // the policy is always reachable.
    function loadPolicy(modal) {
      var body = modal.querySelector('[data-policy-body]');
      if (!body || body.getAttribute('data-loaded')) return;
      var url = body.getAttribute('data-policy-url');
      if (!url) return;
      body.setAttribute('data-loaded', '1');
      fetch(url, { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('policy-' + r.status);
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var el = doc.querySelector('.shopify-policy__body, .rte, main#MainContent, main');
          if (!el || !el.textContent.trim()) throw new Error('policy-empty');
          // Falling through to <main> drags the page's own furniture in with the
          // text — and its <h1> repeats the modal's own heading.
          var clean = el.cloneNode(true);
          clean.querySelectorAll('h1, nav, header, footer, script, style, [class*="breadcrumb"]')
            .forEach(function (n) { n.remove(); });
          body.innerHTML = clean.innerHTML;
        })
        .catch(function () {
          body.setAttribute('data-loaded', ''); // let a second open try again
          body.innerHTML = '<p>We could not load this policy here. ' +
            '<a href="' + url + '" target="_blank" rel="noopener">Open it in a new tab</a>.</p>';
        });
    }

    function closeModals() {
      root.querySelectorAll('.cc-modal').forEach(function (m) { m.hidden = true; });
      document.documentElement.style.overflow = '';
    }
    root.querySelectorAll('[data-policy]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var m = root.querySelector('[data-policy-modal="' + btn.getAttribute('data-policy') + '"]');
        if (m) { m.hidden = false; document.documentElement.style.overflow = 'hidden'; loadPolicy(m); }
      });
    });
    root.querySelectorAll('.cc-modal').forEach(function (m) {
      m.addEventListener('click', function (e) { if (e.target === m) m.hidden = true; });
    });
    root.querySelectorAll('[data-modal-close]').forEach(function (b) {
      b.addEventListener('click', closeModals);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeModals();
    });

    // Mobile: order-summary accordion toggle.
    var sumToggle = root.querySelector('[data-summary-toggle]');
    var sumPanel = $('.cc-sum');
    if (sumToggle && sumPanel) {
      sumToggle.addEventListener('click', function () {
        setTopSummary(!sumPanel.classList.contains('is-open'), false);
      });
    }

    // Mobile recap (above Pay now): tapping it opens + scrolls to the top summary;
    // "Add discount" additionally focuses the promo field.
    var mrecapBar = root.querySelector('[data-mrecap-toggle]');
    if (mrecapBar) mrecapBar.addEventListener('click', toggleRecap);
    var mrecapAdd = root.querySelector('[data-mrecap-adddisc]');
    if (mrecapAdd) mrecapAdd.addEventListener('click', function () {
      openTopSummary();
      var inp = root.querySelector('[name="discount"]');
      if (inp) setTimeout(function () { inp.focus(); }, 350);
    });

    // Selects: float the label only when a value is actually chosen (Shopify behavior).
    root.querySelectorAll('.cc-fl select').forEach(function (sel) {
      function sync() {
        var f = sel.closest('.cc-fl');
        if (!f) return;
        if (sel.value) f.classList.add('cc-fl--filled');
        else f.classList.remove('cc-fl--filled');
      }
      sel.addEventListener('change', sync);
      sync();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
